export interface Question {
  questionNumber: number;
  question: string;
  answerOptions: {
    text: string;
    rationale: string;
    isCorrect: boolean;
  }[];
  hint: string;
}

export const practiceTestQuestions: Question[] = [
  {
    questionNumber: 1,
    question: "Which of the following is considered real property in California?",
    answerOptions: [
      {
        text: "Trade fixtures installed by a tenant",
        rationale: "Trade fixtures are personal property owned by the tenant and removable before lease termination.",
        isCorrect: false
      },
      {
        text: "Minerals beneath the surface",
        rationale: "Mineral, air, and water rights are part of real property unless legally separated.",
        isCorrect: true
      },
      {
        text: "Annual crops planted by a tenant farmer",
        rationale: "Emblements are considered personal property.",
        isCorrect: false
      },
      {
        text: "Portable spa on a deck",
        rationale: "Items not permanently attached are personal property.",
        isCorrect: false
      }
    ],
    hint: "Real property includes land, things attached to it, and legal rights associated with ownership."
  },
  {
    questionNumber: 2,
    question: "A fixture is legally defined as:",
    answerOptions: [
      {
        text: "Personal property attached to real property with the intent to remain permanently",
        rationale: "Once attached with intent to remain, personal property becomes a fixture and part of the real estate.",
        isCorrect: true
      },
      {
        text: "Anything that can be moved from the land without damage",
        rationale: "This describes personal property, not fixtures.",
        isCorrect: false
      },
      {
        text: "Any improvement made by a tenant",
        rationale: "Some tenant improvements are trade fixtures and remain personal property.",
        isCorrect: false
      },
      {
        text: "A structure that has been taxed separately",
        rationale: "Taxation classification does not determine fixture status.",
        isCorrect: false
      }
    ],
    hint: "Look for intent, method of attachment, adaptation, and agreement (the MARIA test)."
  },
  {
    questionNumber: 3,
    question: "Which test is NOT used to determine whether an item is a fixture?",
    answerOptions: [
      {
        text: "Method of attachment",
        rationale: "This is one of the standard MARIA tests for fixtures.",
        isCorrect: false
      },
      {
        text: "Adaptation to the property's use",
        rationale: "This is also part of the MARIA test.",
        isCorrect: false
      },
      {
        text: "Relationship of the parties",
        rationale: "Courts consider this if ownership is disputed.",
        isCorrect: false
      },
      {
        text: "Cost of the item",
        rationale: "Cost is not part of the legal test for a fixture.",
        isCorrect: true
      }
    ],
    hint: "Remember: MARIA = Method, Adaptation, Relationship, Intention, Agreement."
  },
  {
    questionNumber: 4,
    question: "The legal description method that uses bearings, distances, and monuments is known as:",
    answerOptions: [
      {
        text: "Lot and block system",
        rationale: "Used for subdivided urban areas, not rural surveys.",
        isCorrect: false
      },
      {
        text: "Rectangular survey system",
        rationale: "Used in many states but not officially adopted in California.",
        isCorrect: false
      },
      {
        text: "Metes and bounds system",
        rationale: "This method describes property by physical boundaries and compass directions.",
        isCorrect: true
      },
      {
        text: "Geodetic survey system",
        rationale: "Uses latitude and longitude, not commonly used for deeds.",
        isCorrect: false
      }
    ],
    hint: "Think of an old-fashioned surveyor walking the property with a compass."
  },
  {
    questionNumber: 5,
    question: "An easement appurtenant:",
    answerOptions: [
      {
        text: "Benefits the dominant tenement and runs with the land",
        rationale: "It is attached to the land, transferring automatically with ownership.",
        isCorrect: true
      },
      {
        text: "Exists only during the life of the owner",
        rationale: "That describes a personal easement in gross, not appurtenant.",
        isCorrect: false
      },
      {
        text: "Is personal to the grantee and not transferable",
        rationale: "Personal easements do not run with the land.",
        isCorrect: false
      },
      {
        text: "Cannot be terminated by merger",
        rationale: "An easement appurtenant is terminated if both parcels merge under one owner.",
        isCorrect: false
      }
    ],
    hint: "This type of easement involves two parcels — one benefits, one is burdened."
  },
  {
    questionNumber: 6,
    question: "A license differs from an easement because:",
    answerOptions: [
      {
        text: "A license is revocable at any time by the licensor",
        rationale: "Licenses are temporary permissions, not property interests.",
        isCorrect: true
      },
      {
        text: "A license runs with the land and is permanent",
        rationale: "That describes an easement appurtenant.",
        isCorrect: false
      },
      {
        text: "A license must be in writing to be valid",
        rationale: "Licenses can be oral or written.",
        isCorrect: false
      },
      {
        text: "A license is created by grant deed",
        rationale: "Licenses are informal permissions, not deeded interests.",
        isCorrect: false
      }
    ],
    hint: "Think of a ticket to enter property — permission can be withdrawn."
  },
  {
    questionNumber: 7,
    question: "Zoning laws are an example of the government's:",
    answerOptions: [
      {
        text: "Police power",
        rationale: "Zoning regulates land use for public safety and welfare under police power.",
        isCorrect: true
      },
      {
        text: "Eminent domain",
        rationale: "Eminent domain involves taking property for public use with compensation.",
        isCorrect: false
      },
      {
        text: "Escheat",
        rationale: "Escheat applies when property reverts to the state for lack of heirs.",
        isCorrect: false
      },
      {
        text: "Taxation power",
        rationale: "This power concerns revenue, not land use control.",
        isCorrect: false
      }
    ],
    hint: "Police power = the power to regulate for health, safety, and welfare."
  },
  {
    questionNumber: 8,
    question: "Under eminent domain, the government must:",
    answerOptions: [
      {
        text: "Pay just compensation and demonstrate public use",
        rationale: "The 5th Amendment requires fair compensation and public purpose.",
        isCorrect: true
      },
      {
        text: "Provide housing relocation for the displaced owner",
        rationale: "Relocation assistance is separate and not always required by law.",
        isCorrect: false
      },
      {
        text: "Obtain the owner's written consent before taking property",
        rationale: "Consent is not required; condemnation proceedings are legal processes.",
        isCorrect: false
      },
      {
        text: "Pay twice the property's assessed value",
        rationale: "Compensation is based on market value, not assessment.",
        isCorrect: false
      }
    ],
    hint: "Remember: 'taking for public use with just compensation.'"
  },
  {
    questionNumber: 9,
    question: "If a property owner dies intestate with no heirs, ownership transfers to the state through:",
    answerOptions: [
      {
        text: "Escheat",
        rationale: "Escheat prevents property from being ownerless.",
        isCorrect: true
      },
      {
        text: "Eminent domain",
        rationale: "Eminent domain involves taking for public use, not lack of heirs.",
        isCorrect: false
      },
      {
        text: "Condemnation",
        rationale: "Condemnation is the process of taking under eminent domain.",
        isCorrect: false
      },
      {
        text: "Adverse possession",
        rationale: "Adverse possession involves a private claimant, not the state.",
        isCorrect: false
      }
    ],
    hint: "Think of this as the state becoming the 'heir of last resort.'"
  },
  {
    questionNumber: 10,
    question: "A nonconforming use is best described as:",
    answerOptions: [
      {
        text: "A use that was legal when established but does not meet current zoning",
        rationale: "Existing uses may continue until discontinued or destroyed.",
        isCorrect: true
      },
      {
        text: "A use allowed only after obtaining a variance",
        rationale: "A variance allows exceptions to current zoning, not past grandfathered uses.",
        isCorrect: false
      },
      {
        text: "An illegal use that must be immediately stopped",
        rationale: "Nonconforming uses were originally legal and may continue.",
        isCorrect: false
      },
      {
        text: "A type of easement",
        rationale: "Nonconforming uses concern zoning, not ownership rights.",
        isCorrect: false
      }
    ],
    hint: "Grandfathered properties often have this status."
  },
  {
    questionNumber: 11,
    question: "Which of the following is NOT one of the four government powers over land?",
    answerOptions: [
      {
        text: "Police power",
        rationale: "This is one of the four powers - regulating for public welfare.",
        isCorrect: false
      },
      {
        text: "Eminent domain",
        rationale: "This is one of the four powers - taking for public use.",
        isCorrect: false
      },
      {
        text: "Taxation",
        rationale: "This is one of the four powers - raising revenue.",
        isCorrect: false
      },
      {
        text: "Condemnation",
        rationale: "Condemnation is the process of eminent domain, not a separate power.",
        isCorrect: true
      }
    ],
    hint: "The four powers are: Police Power, Eminent Domain, Taxation, and Escheat."
  },
  {
    questionNumber: 12,
    question: "A trade fixture installed by a tenant:",
    answerOptions: [
      {
        text: "Becomes part of the real property immediately upon installation",
        rationale: "Trade fixtures remain personal property of the tenant.",
        isCorrect: false
      },
      {
        text: "Must be removed before the lease expires",
        rationale: "If not removed, it becomes the landlord's property through accession.",
        isCorrect: true
      },
      {
        text: "Cannot be removed under any circumstances",
        rationale: "Tenants have the right to remove trade fixtures.",
        isCorrect: false
      },
      {
        text: "Is automatically owned by the landlord",
        rationale: "Ownership depends on removal timing, not installation.",
        isCorrect: false
      }
    ],
    hint: "Trade fixtures are removable personal property used for business."
  },
  {
    questionNumber: 13,
    question: "The Subdivision Map Act requires:",
    answerOptions: [
      {
        text: "Approval of tentative and final maps for subdivisions",
        rationale: "Both maps must be approved before lots can be sold.",
        isCorrect: true
      },
      {
        text: "Only a final map for any subdivision",
        rationale: "Both tentative and final maps are required.",
        isCorrect: false
      },
      {
        text: "No map approval for subdivisions under 5 lots",
        rationale: "Even small subdivisions require map approval.",
        isCorrect: false
      },
      {
        text: "Only tentative map approval",
        rationale: "Final map approval is also required.",
        isCorrect: false
      }
    ],
    hint: "Think of tentative as 'planning' and final as 'ready to build.'"
  },
  {
    questionNumber: 14,
    question: "An encroachment occurs when:",
    answerOptions: [
      {
        text: "A structure extends beyond property boundaries",
        rationale: "This is the legal definition of an encroachment.",
        isCorrect: true
      },
      {
        text: "A neighbor uses your property with permission",
        rationale: "That describes a license, not an encroachment.",
        isCorrect: false
      },
      {
        text: "Property taxes are not paid",
        rationale: "That leads to tax sale, not encroachment.",
        isCorrect: false
      },
      {
        text: "A building violates zoning laws",
        rationale: "That's a zoning violation, not an encroachment.",
        isCorrect: false
      }
    ],
    hint: "Think of a fence or building crossing the property line."
  },
  {
    questionNumber: 15,
    question: "California Proposition 13 limits property tax increases by:",
    answerOptions: [
      {
        text: "Capping the tax rate at 1% of assessed value",
        rationale: "This is the primary limitation of Proposition 13.",
        isCorrect: true
      },
      {
        text: "Prohibiting all property tax increases",
        rationale: "Taxes can increase up to 2% annually plus new construction.",
        isCorrect: false
      },
      {
        text: "Limiting assessments to market value",
        rationale: "Assessments are limited to purchase price plus inflation.",
        isCorrect: false
      },
      {
        text: "Requiring voter approval for all tax increases",
        rationale: "This applies to special assessments, not regular property taxes.",
        isCorrect: false
      }
    ],
    hint: "Proposition 13 = 1% tax rate + 2% annual increase limit."
  },
  {
    questionNumber: 16,
    question: "A variance allows:",
    answerOptions: [
      {
        text: "An exception to current zoning requirements",
        rationale: "Variances permit uses that don't meet current zoning standards.",
        isCorrect: true
      },
      {
        text: "Continuation of a previously legal use",
        rationale: "That describes a nonconforming use, not a variance.",
        isCorrect: false
      },
      {
        text: "Automatic approval of any development",
        rationale: "Variances require specific justification and public hearings.",
        isCorrect: false
      },
      {
        text: "Permanent change to zoning laws",
        rationale: "Variances are exceptions, not permanent changes.",
        isCorrect: false
      }
    ],
    hint: "Variances are exceptions granted due to hardship or unique circumstances."
  },
  {
    questionNumber: 17,
    question: "The California Environmental Quality Act (CEQA) requires:",
    answerOptions: [
      {
        text: "Environmental impact reports for significant projects",
        rationale: "CEQA mandates environmental review for projects with significant impacts.",
        isCorrect: true
      },
      {
        text: "All development to be environmentally neutral",
        rationale: "CEQA requires review, not necessarily neutrality.",
        isCorrect: false
      },
      {
        text: "Only residential projects to have environmental review",
        rationale: "CEQA applies to all types of projects, not just residential.",
        isCorrect: false
      },
      {
        text: "Automatic approval of environmentally friendly projects",
        rationale: "All projects require proper environmental review process.",
        isCorrect: false
      }
    ],
    hint: "CEQA = California's environmental protection law requiring impact studies."
  },
  {
    questionNumber: 18,
    question: "Water rights in California are governed by:",
    answerOptions: [
      {
        text: "The doctrine of prior appropriation",
        rationale: "California uses 'first in time, first in right' for water allocation.",
        isCorrect: true
      },
      {
        text: "Riparian rights only",
        rationale: "California uses both riparian and appropriative rights.",
        isCorrect: false
      },
      {
        text: "Federal water law exclusively",
        rationale: "California has its own water rights system.",
        isCorrect: false
      },
      {
        text: "Local government regulations only",
        rationale: "Water rights are governed by state law, not just local rules.",
        isCorrect: false
      }
    ],
    hint: "California water law = 'first come, first served' system."
  },
  {
    questionNumber: 19,
    question: "A restrictive covenant:",
    answerOptions: [
      {
        text: "Limits how property may be used",
        rationale: "Restrictive covenants impose use limitations on property.",
        isCorrect: true
      },
      {
        text: "Grants additional rights to property owners",
        rationale: "Covenants restrict, not grant, rights.",
        isCorrect: false
      },
      {
        text: "Is only enforceable by government agencies",
        rationale: "Private parties can enforce restrictive covenants.",
        isCorrect: false
      },
      {
        text: "Expires automatically after 10 years",
        rationale: "Restrictive covenants can run indefinitely with the land.",
        isCorrect: false
      }
    ],
    hint: "Think of CC&Rs (Covenants, Conditions & Restrictions) in subdivisions."
  },
  {
    questionNumber: 20,
    question: "The Subdivided Lands Law requires:",
    answerOptions: [
      {
        text: "A public report before selling subdivided lots",
        rationale: "The DRE must issue a public report before lot sales begin.",
        isCorrect: true
      },
      {
        text: "Only final map approval",
        rationale: "Both map approval and public report are required.",
        isCorrect: false
      },
      {
        text: "No disclosure for subdivisions under 10 lots",
        rationale: "All subdivisions require public reports regardless of size.",
        isCorrect: false
      },
      {
        text: "Automatic approval after 30 days",
        rationale: "Public reports require DRE review and approval.",
        isCorrect: false
      }
    ],
    hint: "Public report = disclosure document required before selling subdivided lots."
  },
  {
    questionNumber: 21,
    question: "An easement by necessity is created when:",
    answerOptions: [
      {
        text: "A landlocked parcel needs access to a public road",
        rationale: "Courts will imply an easement for landlocked properties.",
        isCorrect: true
      },
      {
        text: "A property owner wants a scenic view",
        rationale: "Necessity easements are for essential access, not views.",
        isCorrect: false
      },
      {
        text: "A neighbor requests permission to cross property",
        rationale: "That creates a license, not an easement by necessity.",
        isCorrect: false
      },
      {
        text: "A property is sold without access rights",
        rationale: "Easements by necessity arise from landlocked situations.",
        isCorrect: false
      }
    ],
    hint: "Necessity = when there's no other way to access the property."
  },
  {
    questionNumber: 22,
    question: "California's Williamson Act provides:",
    answerOptions: [
      {
        text: "Tax incentives for agricultural land preservation",
        rationale: "Landowners receive tax breaks for keeping land in agriculture.",
        isCorrect: true
      },
      {
        text: "Automatic rezoning of agricultural land",
        rationale: "The Act preserves agricultural zoning, not changes it.",
        isCorrect: false
      },
      {
        text: "Mandatory conversion of farmland to housing",
        rationale: "The Act prevents conversion, not mandates it.",
        isCorrect: false
      },
      {
        text: "Free water rights for farmers",
        rationale: "The Act concerns land use, not water rights.",
        isCorrect: false
      }
    ],
    hint: "Williamson Act = California's farmland preservation program."
  },
  {
    questionNumber: 23,
    question: "A prescriptive easement requires:",
    answerOptions: [
      {
        text: "Open, notorious, continuous, and hostile use for the statutory period",
        rationale: "These are the four elements required for prescriptive easements.",
        isCorrect: true
      },
      {
        text: "Written permission from the property owner",
        rationale: "Permission negates the hostile requirement.",
        isCorrect: false
      },
      {
        text: "Only 3 years of use",
        rationale: "California requires 5 years for prescriptive easements.",
        isCorrect: false
      },
      {
        text: "Payment of rent to the property owner",
        rationale: "Payment would create a license, not a prescriptive easement.",
        isCorrect: false
      }
    ],
    hint: "Prescriptive = adverse possession for use rights, not ownership."
  },
  {
    questionNumber: 24,
    question: "A building permit is required for:",
    answerOptions: [
      {
        text: "Any structural modification to a building",
        rationale: "Building permits are required for structural changes, additions, and major renovations.",
        isCorrect: true
      },
      {
        text: "Only new construction over 1,000 square feet",
        rationale: "Permits are required for most construction work regardless of size.",
        isCorrect: false
      },
      {
        text: "Painting and cosmetic repairs only",
        rationale: "Cosmetic work typically doesn't require permits.",
        isCorrect: false
      },
      {
        text: "Commercial buildings but not residential",
        rationale: "Both residential and commercial construction require permits.",
        isCorrect: false
      }
    ],
    hint: "Building permits ensure construction meets safety and zoning requirements."
  },
  {
    questionNumber: 25,
    question: "The California Coastal Act regulates development:",
    answerOptions: [
      {
        text: "Within 1,000 yards of the coastline",
        rationale: "The Coastal Act protects coastal resources and public access.",
        isCorrect: true
      },
      {
        text: "Only on beaches and sand dunes",
        rationale: "The Act covers broader coastal areas, not just beaches.",
        isCorrect: false
      },
      {
        text: "Exclusively for commercial properties",
        rationale: "The Act applies to all development in the coastal zone.",
        isCorrect: false
      },
      {
        text: "Only during summer months",
        rationale: "The Act applies year-round, not seasonally.",
        isCorrect: false
      }
    ],
    hint: "Coastal Act = protection of California's coastline and public access."
  },
  {
    questionNumber: 26,
    question: "In California, an agency relationship is created when:",
    answerOptions: [
      {
        text: "A principal and agent agree, either in writing or orally, that the agent will act on the principal's behalf",
        rationale: "Agency can be created by agreement—oral or written—if authorized by the principal.",
        isCorrect: true
      },
      {
        text: "A buyer visits an open house and speaks to the listing agent",
        rationale: "Casual conversation does not create agency.",
        isCorrect: false
      },
      {
        text: "The broker advertises the property online",
        rationale: "Advertising shows intent to find clients but does not create agency.",
        isCorrect: false
      },
      {
        text: "A salesperson gives general information about a listing",
        rationale: "Providing information is not representation or agency creation.",
        isCorrect: false
      }
    ],
    hint: "Agency is based on consent, not just contact."
  },
  {
    questionNumber: 27,
    question: "A real estate agent owes fiduciary duties to:",
    answerOptions: [
      {
        text: "Their principal only",
        rationale: "Fiduciary duties—loyalty, confidentiality, obedience—are owed exclusively to the client.",
        isCorrect: true
      },
      {
        text: "All parties to a transaction equally",
        rationale: "Agents owe honesty and fairness to all but fiduciary duties only to their client.",
        isCorrect: false
      },
      {
        text: "The Department of Real Estate",
        rationale: "The DRE regulates licensees but is not a client.",
        isCorrect: false
      },
      {
        text: "The lender",
        rationale: "Lenders are third parties, not principals to agency relationships.",
        isCorrect: false
      }
    ],
    hint: "Think: fiduciary = client loyalty."
  },
  {
    questionNumber: 28,
    question: "The term 'fiduciary' means:",
    answerOptions: [
      {
        text: "A person in a position of trust and confidence",
        rationale: "Agents act as fiduciaries for their clients, putting the client's interests first.",
        isCorrect: true
      },
      {
        text: "A person employed to perform manual tasks",
        rationale: "This describes a servant or employee, not a fiduciary.",
        isCorrect: false
      },
      {
        text: "An independent contractor paid hourly",
        rationale: "Fiduciary status is based on trust, not pay method.",
        isCorrect: false
      },
      {
        text: "A neutral third party in escrow",
        rationale: "Escrow holders are neutral but not fiduciaries of either party.",
        isCorrect: false
      }
    ],
    hint: "Derived from the Latin 'fiducia,' meaning trust."
  },
  {
    questionNumber: 29,
    question: "Which of the following is NOT a fiduciary duty owed to a client?",
    answerOptions: [
      {
        text: "Disclosure of all material facts",
        rationale: "Agents must disclose all material facts to their client.",
        isCorrect: false
      },
      {
        text: "Loyalty to the principal's interest",
        rationale: "Loyalty is a core fiduciary obligation.",
        isCorrect: false
      },
      {
        text: "Obedience to lawful instructions",
        rationale: "Agents must follow all legal instructions from their client.",
        isCorrect: false
      },
      {
        text: "Providing legal advice regarding contract disputes",
        rationale: "Agents cannot practice law; this is outside their fiduciary role.",
        isCorrect: true
      }
    ],
    hint: "Fiduciary duties are about trust, care, and loyalty—not legal representation."
  },
  {
    questionNumber: 30,
    question: "Dual agency in California is:",
    answerOptions: [
      {
        text: "Legal if disclosed to and consented by both parties in writing",
        rationale: "Disclosure and consent from both sides are required under Civil Code §2079.16.",
        isCorrect: true
      },
      {
        text: "Illegal under any circumstances",
        rationale: "Dual agency is permitted with disclosure and consent.",
        isCorrect: false
      },
      {
        text: "Allowed without disclosure if broker represents both sides through different salespersons",
        rationale: "Salespersons' actions are the broker's actions; disclosure is still required.",
        isCorrect: false
      },
      {
        text: "Only allowed in commercial transactions",
        rationale: "Rules apply to all property types.",
        isCorrect: false
      }
    ],
    hint: "The key word is 'disclosed and written consent.'"
  },
  {
    questionNumber: 31,
    question: "When must an agency disclosure form be given to a seller?",
    answerOptions: [
      {
        text: "Before signing the listing agreement",
        rationale: "Disclosure must be made prior to the client committing to representation.",
        isCorrect: true
      },
      {
        text: "At closing",
        rationale: "Too late—agency must be disclosed before agency relationship is formed.",
        isCorrect: false
      },
      {
        text: "Before presenting an offer",
        rationale: "Disclosure should already have been made at the listing stage.",
        isCorrect: false
      },
      {
        text: "Within 3 days after the property is listed",
        rationale: "There's no grace period—disclosure is required upfront.",
        isCorrect: false
      }
    ],
    hint: "Disclosure happens before the client signs anything binding."
  },
  {
    questionNumber: 32,
    question: "A real estate broker represents both buyer and seller with written consent. What must the broker NOT do?",
    answerOptions: [
      {
        text: "Disclose confidential information from one party to the other",
        rationale: "Confidentiality remains even in dual agency.",
        isCorrect: true
      },
      {
        text: "Act with reasonable skill and care toward both parties",
        rationale: "That duty remains for both sides.",
        isCorrect: false
      },
      {
        text: "Disclose known material facts affecting the property's value",
        rationale: "Required regardless of agency type.",
        isCorrect: false
      },
      {
        text: "Assist both parties in completing the transaction",
        rationale: "Permitted under dual agency with consent.",
        isCorrect: false
      }
    ],
    hint: "Even in dual agency, confidentiality still applies."
  },
  {
    questionNumber: 33,
    question: "An agency relationship may be terminated by all EXCEPT:",
    answerOptions: [
      {
        text: "Completion of the purpose",
        rationale: "Agency ends when its objective is achieved.",
        isCorrect: false
      },
      {
        text: "Expiration of the term",
        rationale: "Agency expires at the stated time limit.",
        isCorrect: false
      },
      {
        text: "Death of the broker or principal",
        rationale: "Ends the relationship automatically by law.",
        isCorrect: false
      },
      {
        text: "Withdrawal of an offer by a buyer",
        rationale: "Does not terminate the listing contract between seller and broker.",
        isCorrect: true
      }
    ],
    hint: "Termination relates to the agency agreement itself, not a particular offer."
  },
  {
    questionNumber: 34,
    question: "A salesperson's commission is payable:",
    answerOptions: [
      {
        text: "Only by their employing broker",
        rationale: "Salespersons are paid by their broker, not clients or other brokers.",
        isCorrect: true
      },
      {
        text: "Directly by the buyer or seller",
        rationale: "This violates DRE rules; compensation must flow through the broker.",
        isCorrect: false
      },
      {
        text: "By escrow at closing upon request",
        rationale: "Escrow disburses funds to the broker, not directly to the salesperson.",
        isCorrect: false
      },
      {
        text: "By the MLS upon confirmation of sale",
        rationale: "MLS does not handle payments.",
        isCorrect: false
      }
    ],
    hint: "Remember: the broker controls compensation."
  },
  {
    questionNumber: 35,
    question: "A salesperson acts as a(n):",
    answerOptions: [
      {
        text: "Agent of the broker and subagent of the broker's client",
        rationale: "Salespersons represent their broker, who represents the client.",
        isCorrect: true
      },
      {
        text: "Independent agent of the principal",
        rationale: "Salespersons cannot act independently of their broker.",
        isCorrect: false
      },
      {
        text: "General agent of the public",
        rationale: "Agency is specific to clients, not the public.",
        isCorrect: false
      },
      {
        text: "Trustee of the escrow account",
        rationale: "Escrow holders are independent third parties, not agents.",
        isCorrect: false
      }
    ],
    hint: "Salespersons represent their broker, not the client directly."
  },
  {
    questionNumber: 36,
    question: "A real estate broker must retain copies of agency disclosure forms for:",
    answerOptions: [
      {
        text: "Three years from the date of closing or listing",
        rationale: "DRE regulations require retention for three years.",
        isCorrect: true
      },
      {
        text: "One year after closing",
        rationale: "Too short per state requirements.",
        isCorrect: false
      },
      {
        text: "Five years after execution",
        rationale: "The law mandates three, not five.",
        isCorrect: false
      },
      {
        text: "Until the buyer takes possession",
        rationale: "Retention is independent of possession.",
        isCorrect: false
      }
    ],
    hint: "Three-year record retention is a core DRE rule."
  },
  {
    questionNumber: 37,
    question: "A buyer's agent owes fiduciary duties to:",
    answerOptions: [
      {
        text: "The buyer only",
        rationale: "The buyer is the principal in this agency relationship.",
        isCorrect: true
      },
      {
        text: "The seller",
        rationale: "The seller is a third party unless dual agency is disclosed.",
        isCorrect: false
      },
      {
        text: "The listing broker",
        rationale: "Agents cooperate but do not owe fiduciary duties to each other.",
        isCorrect: false
      },
      {
        text: "Both buyer and seller automatically",
        rationale: "Dual agency requires consent; not automatic.",
        isCorrect: false
      }
    ],
    hint: "Fiduciary duties always flow to the principal."
  },
  {
    questionNumber: 38,
    question: "Which of the following best describes an ostensible agency?",
    answerOptions: [
      {
        text: "An agency created by actions that lead others to believe it exists",
        rationale: "Also known as apparent agency—formed by conduct, not formal agreement.",
        isCorrect: true
      },
      {
        text: "An agency created in writing by the principal",
        rationale: "That's an express agency.",
        isCorrect: false
      },
      {
        text: "An agency implied by law after death",
        rationale: "Agency terminates upon death; it cannot arise afterward.",
        isCorrect: false
      },
      {
        text: "An illegal agency arrangement",
        rationale: "Ostensible agencies are legal if the principal's conduct creates them.",
        isCorrect: false
      }
    ],
    hint: "'Ostensible' means 'apparent'—it looks like agency exists."
  },
  {
    questionNumber: 39,
    question: "The primary duty of a listing agent is to:",
    answerOptions: [
      {
        text: "Obtain the highest price for the seller's property",
        rationale: "The listing agent must act in the seller's best interest.",
        isCorrect: true
      },
      {
        text: "Treat both parties equally",
        rationale: "Honesty and fairness to all—but loyalty to client only.",
        isCorrect: false
      },
      {
        text: "Ensure the buyer obtains financing",
        rationale: "That's the buyer's and lender's responsibility.",
        isCorrect: false
      },
      {
        text: "Appraise the property for loan purposes",
        rationale: "Agents can provide CMA, not appraisals.",
        isCorrect: false
      }
    ],
    hint: "Who is the client in a listing?"
  },
  {
    questionNumber: 40,
    question: "A broker acting as a dual agent must:",
    answerOptions: [
      {
        text: "Provide the same fiduciary duties to both parties except undivided loyalty and full disclosure of confidential information",
        rationale: "Dual agents must remain neutral and protect confidentiality.",
        isCorrect: true
      },
      {
        text: "Favor the seller since they pay the commission",
        rationale: "Compensation does not determine loyalty.",
        isCorrect: false
      },
      {
        text: "Keep all information confidential, including material facts",
        rationale: "Material facts must always be disclosed.",
        isCorrect: false
      },
      {
        text: "Avoid giving either party a copy of the disclosure form",
        rationale: "Disclosure is required to both.",
        isCorrect: false
      }
    ],
    hint: "Dual agency = balance + disclosure."
  },
  {
    questionNumber: 41,
    question: "A real estate broker who represents a buyer must disclose material facts known about the property to:",
    answerOptions: [
      {
        text: "Both the buyer and the seller",
        rationale: "Material facts affecting value or desirability must be disclosed to all parties, even if it harms the broker's client.",
        isCorrect: true
      },
      {
        text: "Only the buyer, since they are the client",
        rationale: "Material facts must be disclosed to all parties involved in a transaction.",
        isCorrect: false
      },
      {
        text: "Only the seller, since they own the property",
        rationale: "Sellers already know about their property; disclosure helps the buyer make an informed decision.",
        isCorrect: false
      },
      {
        text: "The escrow company only",
        rationale: "Escrow holders are neutral third parties and not recipients of material disclosures.",
        isCorrect: false
      }
    ],
    hint: "Material facts always get disclosed to everyone — no secrets here."
  },
  {
    questionNumber: 42,
    question: "An agent is showing a property and learns that a recent death occurred on-site over two years ago. What must the agent do?",
    answerOptions: [
      {
        text: "Disclose the death only if asked by the buyer",
        rationale: "Under California Civil Code §1710.2, deaths more than 3 years old need not be disclosed unless specifically asked.",
        isCorrect: true
      },
      {
        text: "Disclose the death regardless of timing",
        rationale: "Only deaths within the past 3 years must be disclosed automatically.",
        isCorrect: false
      },
      {
        text: "Never disclose a death on property under any circumstance",
        rationale: "That would violate the duty of honesty if asked directly.",
        isCorrect: false
      },
      {
        text: "Disclose it only in writing before closing",
        rationale: "Disclosure timing isn't limited to closing; verbal response is sufficient when asked.",
        isCorrect: false
      }
    ],
    hint: "Remember the '3-year rule' for on-site deaths in California."
  },
  {
    questionNumber: 43,
    question: "Which of the following must a listing agent disclose to a prospective buyer?",
    answerOptions: [
      {
        text: "All known material facts affecting the property's value or desirability",
        rationale: "This duty applies to both the client and third parties.",
        isCorrect: true
      },
      {
        text: "Only defects found in the home inspection report",
        rationale: "Disclosure must include all known defects, not just those found later.",
        isCorrect: false
      },
      {
        text: "The seller's reason for moving",
        rationale: "Only if it affects the property's value or desirability.",
        isCorrect: false
      },
      {
        text: "The seller's bottom-line price",
        rationale: "Confidential information that cannot be disclosed without permission.",
        isCorrect: false
      }
    ],
    hint: "Material = affects decision to buy or price offered."
  },
  {
    questionNumber: 44,
    question: "An unlicensed assistant may legally perform which of the following activities?",
    answerOptions: [
      {
        text: "Distribute pre-printed advertising materials",
        rationale: "Clerical and marketing support activities are permitted.",
        isCorrect: true
      },
      {
        text: "Negotiate terms of a lease on behalf of a broker",
        rationale: "Negotiation requires a license.",
        isCorrect: false
      },
      {
        text: "Show property to prospective buyers",
        rationale: "Only licensees may show or discuss property details.",
        isCorrect: false
      },
      {
        text: "Discuss financing options with clients",
        rationale: "Unlicensed persons cannot advise on loans or contracts.",
        isCorrect: false
      }
    ],
    hint: "Unlicensed = administrative only."
  },
  {
    questionNumber: 45,
    question: "A broker must place all trust funds into the trust account:",
    answerOptions: [
      {
        text: "Within three business days after receipt",
        rationale: "California law requires deposit within three business days of receipt unless directed otherwise in writing.",
        isCorrect: true
      },
      {
        text: "Within 24 hours of receipt",
        rationale: "That's stricter than required by law.",
        isCorrect: false
      },
      {
        text: "Within five calendar days",
        rationale: "Too long — the rule is three business days.",
        isCorrect: false
      },
      {
        text: "Immediately after acceptance of the offer",
        rationale: "Timing is based on receipt, not acceptance.",
        isCorrect: false
      }
    ],
    hint: "Think: 'three-day trust fund rule.'"
  },
  {
    questionNumber: 46,
    question: "Commingling occurs when:",
    answerOptions: [
      {
        text: "A broker mixes client funds with personal or business funds",
        rationale: "Client funds must always be kept separate in a trust account.",
        isCorrect: true
      },
      {
        text: "A broker deposits client checks into an escrow account",
        rationale: "That's correct procedure if directed by contract.",
        isCorrect: false
      },
      {
        text: "A broker delays depositing an earnest money check for three days",
        rationale: "Delays may be improper, but that's not commingling.",
        isCorrect: false
      },
      {
        text: "A broker deposits their commission into the trust account",
        rationale: "Broker commissions should not go into the trust account either.",
        isCorrect: false
      }
    ],
    hint: "Mixing money = commingling."
  },
  {
    questionNumber: 47,
    question: "A broker who intentionally keeps a client's earnest money deposit for personal use is guilty of:",
    answerOptions: [
      {
        text: "Conversion",
        rationale: "Conversion is the unlawful use of another's funds for personal benefit.",
        isCorrect: true
      },
      {
        text: "Commingling",
        rationale: "Commingling is mixing funds, not spending them.",
        isCorrect: false
      },
      {
        text: "Negligence",
        rationale: "Negligence implies carelessness, not intent.",
        isCorrect: false
      },
      {
        text: "Misrepresentation",
        rationale: "Misrepresentation involves false statements, not theft.",
        isCorrect: false
      }
    ],
    hint: "Conversion = criminal misuse of trust funds."
  },
  {
    questionNumber: 48,
    question: "The Real Estate Recovery Fund is designed to:",
    answerOptions: [
      {
        text: "Compensate members of the public financially harmed by licensed agents' fraud or conversion",
        rationale: "Administered by the DRE, the fund reimburses victims up to statutory limits.",
        isCorrect: true
      },
      {
        text: "Provide insurance for broker commissions",
        rationale: "The fund compensates the public, not licensees.",
        isCorrect: false
      },
      {
        text: "Subsidize housing for low-income buyers",
        rationale: "Unrelated to consumer compensation.",
        isCorrect: false
      },
      {
        text: "Fund DRE educational programs",
        rationale: "Funded by license fees, but purpose is consumer protection.",
        isCorrect: false
      }
    ],
    hint: "Think of this as the consumer's last resort safety net."
  },
  {
    questionNumber: 49,
    question: "An agent must present all written offers to the seller:",
    answerOptions: [
      {
        text: "Immediately, until the seller accepts an offer or instructs otherwise in writing",
        rationale: "All offers must be promptly presented unless directed otherwise in writing by the seller.",
        isCorrect: true
      },
      {
        text: "Only after verifying buyer's financing approval",
        rationale: "Agents must present all offers regardless of qualification unless instructed otherwise.",
        isCorrect: false
      },
      {
        text: "Within 48 hours of receipt",
        rationale: "There's no fixed time frame; offers must be presented promptly.",
        isCorrect: false
      },
      {
        text: "Only if the offer is close to the asking price",
        rationale: "All offers must be presented, regardless of price.",
        isCorrect: false
      }
    ],
    hint: "The seller—not the agent—decides what offers to consider."
  },
  {
    questionNumber: 50,
    question: "A real estate agent must disclose their license status when:",
    answerOptions: [
      {
        text: "Buying or selling property for themselves or a family member",
        rationale: "Agents must disclose that they are licensed when acting as principal or on behalf of relatives to avoid conflicts of interest.",
        isCorrect: true
      },
      {
        text: "Advertising another agent's listing",
        rationale: "Must follow advertising rules, but license disclosure not required.",
        isCorrect: false
      },
      {
        text: "Showing a property for their broker",
        rationale: "Already known within agency representation.",
        isCorrect: false
      },
      {
        text: "Attending open houses",
        rationale: "Disclosure applies to transactions, not attendance.",
        isCorrect: false
      }
    ],
    hint: "Transparency prevents self-dealing violations."
  },
  {
    questionNumber: 51,
    question: "The process of estimating the value of a property based on recent sales of similar properties is called the:",
    answerOptions: [
      {
        text: "Sales comparison approach",
        rationale: "Also known as the market data approach, it compares similar properties recently sold.",
        isCorrect: true
      },
      {
        text: "Cost approach",
        rationale: "This approach estimates replacement cost minus depreciation.",
        isCorrect: false
      },
      {
        text: "Income approach",
        rationale: "Used primarily for income-producing property.",
        isCorrect: false
      },
      {
        text: "Residual approach",
        rationale: "Used to value land or building separately in development analysis.",
        isCorrect: false
      }
    ],
    hint: "Think of what comparable sales reveal about current market behavior."
  },
  {
    questionNumber: 52,
    question: "Which approach to value is most reliable for a new single-family home?",
    answerOptions: [
      {
        text: "Cost approach",
        rationale: "For new construction, cost closely approximates market value.",
        isCorrect: true
      },
      {
        text: "Income approach",
        rationale: "Usually applied to rental and commercial properties.",
        isCorrect: false
      },
      {
        text: "Gross rent multiplier",
        rationale: "Useful for rentals, not owner-occupied homes.",
        isCorrect: false
      },
      {
        text: "Sales comparison approach",
        rationale: "Helpful but less precise when limited comparable sales exist for new homes.",
        isCorrect: false
      }
    ],
    hint: "If it's brand-new, cost = value benchmark."
  },
  {
    questionNumber: 53,
    question: "The income approach is based primarily on the principle of:",
    answerOptions: [
      {
        text: "Anticipation",
        rationale: "Value is based on expected future income streams.",
        isCorrect: true
      },
      {
        text: "Contribution",
        rationale: "Applies to component improvements, not income projections.",
        isCorrect: false
      },
      {
        text: "Substitution",
        rationale: "Core of the market data approach, not income approach.",
        isCorrect: false
      },
      {
        text: "Regression",
        rationale: "Describes how larger homes may lose value near smaller ones.",
        isCorrect: false
      }
    ],
    hint: "Income = future benefits anticipated."
  },
  {
    questionNumber: 54,
    question: "An appraiser estimating replacement cost of a structure uses which of the following methods?",
    answerOptions: [
      {
        text: "Square-foot method",
        rationale: "Estimates cost per square foot based on comparable new buildings.",
        isCorrect: true
      },
      {
        text: "Land residual method",
        rationale: "Used for separating land and building values, not replacement cost directly.",
        isCorrect: false
      },
      {
        text: "Capitalization method",
        rationale: "Used in the income approach.",
        isCorrect: false
      },
      {
        text: "Comparative unit method",
        rationale: "Similar conceptually but less precise terminology for appraisal standards.",
        isCorrect: false
      }
    ],
    hint: "Most common shortcut for new building cost estimates."
  },
  {
    questionNumber: 55,
    question: "Economic obsolescence in a property's value is generally caused by:",
    answerOptions: [
      {
        text: "Factors outside the property, such as nearby nuisances or declining neighborhood",
        rationale: "External forces beyond owner control create economic obsolescence.",
        isCorrect: true
      },
      {
        text: "Poor design of floor plan",
        rationale: "That's functional obsolescence.",
        isCorrect: false
      },
      {
        text: "Deferred maintenance like peeling paint",
        rationale: "That's physical deterioration.",
        isCorrect: false
      },
      {
        text: "Overimprovement of the property",
        rationale: "Still functional, not economic, obsolescence.",
        isCorrect: false
      }
    ],
    hint: "External = economic."
  },
  {
    questionNumber: 56,
    question: "Which principle of value states that a buyer will not pay more for a property than for an equally desirable substitute?",
    answerOptions: [
      {
        text: "Substitution",
        rationale: "The foundation of the market data approach.",
        isCorrect: true
      },
      {
        text: "Progression",
        rationale: "Smaller homes gain value near larger ones.",
        isCorrect: false
      },
      {
        text: "Regression",
        rationale: "Larger homes lose value near smaller ones.",
        isCorrect: false
      },
      {
        text: "Contribution",
        rationale: "Focuses on individual components, not alternatives.",
        isCorrect: false
      }
    ],
    hint: "Substitute equals competition equals price ceiling."
  },
  {
    questionNumber: 57,
    question: "Functional obsolescence could result from:",
    answerOptions: [
      {
        text: "An outdated kitchen layout",
        rationale: "Design inefficiency or lack of modern utility creates functional obsolescence.",
        isCorrect: true
      },
      {
        text: "Termite damage",
        rationale: "That's physical deterioration.",
        isCorrect: false
      },
      {
        text: "A nearby industrial plant",
        rationale: "That's external (economic) obsolescence.",
        isCorrect: false
      },
      {
        text: "A rise in interest rates",
        rationale: "Affects demand, not structure functionality.",
        isCorrect: false
      }
    ],
    hint: "Think 'out of date' rather than 'worn out.'"
  },
  {
    questionNumber: 58,
    question: "If comparable sales have features superior to the subject property, the appraiser should:",
    answerOptions: [
      {
        text: "Subtract value from the comparables",
        rationale: "Adjustments always move toward the subject property's characteristics.",
        isCorrect: true
      },
      {
        text: "Add value to the comparables",
        rationale: "That would make them even less comparable.",
        isCorrect: false
      },
      {
        text: "Ignore differences if within 10% of price",
        rationale: "Adjustments are always made for material differences.",
        isCorrect: false
      },
      {
        text: "Adjust the subject property instead",
        rationale: "Appraisers adjust the comparables, not the subject.",
        isCorrect: false
      }
    ],
    hint: "Adjust the comps, not the subject."
  },
  {
    questionNumber: 59,
    question: "When using the cost approach, land value is determined by:",
    answerOptions: [
      {
        text: "Market comparison of similar vacant sites",
        rationale: "Land is valued separately through sales comparison of comparable lots.",
        isCorrect: true
      },
      {
        text: "Depreciated replacement cost",
        rationale: "That applies to improvements, not land.",
        isCorrect: false
      },
      {
        text: "Capitalization of land income",
        rationale: "Rarely used except for leased land valuations.",
        isCorrect: false
      },
      {
        text: "Assessed tax value",
        rationale: "Tax assessments may not reflect market value.",
        isCorrect: false
      }
    ],
    hint: "Land stands alone — always valued as vacant."
  },
  {
    questionNumber: 60,
    question: "The formula for capitalization rate (Cap Rate) is:",
    answerOptions: [
      {
        text: "Net operating income ÷ value",
        rationale: "Cap rate = NOI ÷ Value; it expresses return on investment.",
        isCorrect: true
      },
      {
        text: "Gross income ÷ value",
        rationale: "Gross income must be reduced by expenses to get NOI.",
        isCorrect: false
      },
      {
        text: "Value ÷ net income",
        rationale: "That's the inverse of the correct formula.",
        isCorrect: false
      },
      {
        text: "Operating expenses ÷ gross income",
        rationale: "That's an expense ratio, not capitalization rate.",
        isCorrect: false
      }
    ],
    hint: "Think: income over value = rate."
  },
  {
    questionNumber: 61,
    question: "If a property produces an annual NOI of $60,000 and the capitalization rate is 10%, the estimated value is:",
    answerOptions: [
      {
        text: "$600,000",
        rationale: "Value = NOI ÷ Cap Rate → $60,000 ÷ 0.10 = $600,000.",
        isCorrect: true
      },
      {
        text: "$500,000",
        rationale: "Would be correct for a 12% cap rate, not 10%.",
        isCorrect: false
      },
      {
        text: "$650,000",
        rationale: "Mathematical error; overvalues property.",
        isCorrect: false
      },
      {
        text: "$720,000",
        rationale: "Corresponds to an 8.3% cap rate.",
        isCorrect: false
      }
    ],
    hint: "Cap Rate = NOI ÷ Value ⇒ rearrange for Value = NOI ÷ Cap Rate."
  },
  {
    questionNumber: 62,
    question: "The principle of contribution states that:",
    answerOptions: [
      {
        text: "The value of a component is measured by how much it adds to or detracts from overall value",
        rationale: "A swimming pool's cost doesn't equal its contribution to value.",
        isCorrect: true
      },
      {
        text: "Each part of a property must have equal value",
        rationale: "Contribution doesn't require equality.",
        isCorrect: false
      },
      {
        text: "Property values rise together with neighboring values",
        rationale: "That's progression.",
        isCorrect: false
      },
      {
        text: "Older properties contribute more than new ones",
        rationale: "No such rule exists.",
        isCorrect: false
      }
    ],
    hint: "Think marginal value added by each feature."
  },
  {
    questionNumber: 63,
    question: "In appraisal, 'highest and best use' means:",
    answerOptions: [
      {
        text: "The most profitable legal use that is physically possible and financially feasible",
        rationale: "It's the cornerstone concept of land valuation.",
        isCorrect: true
      },
      {
        text: "The current use of the property",
        rationale: "Not necessarily the highest or best.",
        isCorrect: false
      },
      {
        text: "The use preferred by the owner",
        rationale: "Personal preference doesn't determine market value.",
        isCorrect: false
      },
      {
        text: "The use allowed by local custom",
        rationale: "Must be legally permissible, not customary.",
        isCorrect: false
      }
    ],
    hint: "Feasible, legal, and profitable = highest and best."
  },
  {
    questionNumber: 64,
    question: "Gross Rent Multiplier (GRM) is calculated by dividing:",
    answerOptions: [
      {
        text: "Sale price by monthly or annual gross rent",
        rationale: "GRM = Price ÷ Rent; a quick value estimator for small rentals.",
        isCorrect: true
      },
      {
        text: "Net income by value",
        rationale: "That's a capitalization rate, not GRM.",
        isCorrect: false
      },
      {
        text: "Operating expenses by rent",
        rationale: "That's an expense ratio.",
        isCorrect: false
      },
      {
        text: "Rent by market value",
        rationale: "Inverse of the correct formula.",
        isCorrect: false
      }
    ],
    hint: "Price ÷ Rent = GRM."
  },
  {
    questionNumber: 65,
    question: "The sales comparison approach relies heavily on:",
    answerOptions: [
      {
        text: "Recently sold comparable properties in the same area",
        rationale: "The best comparables are recent, nearby, and similar.",
        isCorrect: true
      },
      {
        text: "Replacement cost of improvements",
        rationale: "Used in the cost approach.",
        isCorrect: false
      },
      {
        text: "Expected future income",
        rationale: "Used in the income approach.",
        isCorrect: false
      },
      {
        text: "Assessed property tax values",
        rationale: "Assessments are not reliable market indicators.",
        isCorrect: false
      }
    ],
    hint: "Comparable sales = the backbone of residential appraisal."
  },
  {
    questionNumber: 66,
    question: "Which of the following is NOT a step in the appraisal process?",
    answerOptions: [
      {
        text: "Reconciling the values from different approaches",
        rationale: "Reconciliation is the final step.",
        isCorrect: false
      },
      {
        text: "Estimating the highest and best use",
        rationale: "Determined early in the process.",
        isCorrect: false
      },
      {
        text: "Averaging the indicated values to reach a conclusion",
        rationale: "Values are reconciled, not averaged.",
        isCorrect: true
      },
      {
        text: "Gathering and verifying data",
        rationale: "A necessary early step.",
        isCorrect: false
      }
    ],
    hint: "Appraisers reconcile — they don't simply average."
  },
  {
    questionNumber: 67,
    question: "An appraiser's opinion of value is valid for:",
    answerOptions: [
      {
        text: "The date stated in the appraisal report",
        rationale: "Value is specific to a certain effective date.",
        isCorrect: true
      },
      {
        text: "Six months from the date of inspection",
        rationale: "No fixed validity period exists.",
        isCorrect: false
      },
      {
        text: "Until property taxes are reassessed",
        rationale: "Taxes are unrelated to appraisal validity.",
        isCorrect: false
      },
      {
        text: "Until a new appraisal is ordered",
        rationale: "A new appraisal may supersede, but validity depends on effective date.",
        isCorrect: false
      }
    ],
    hint: "Appraisals are snapshots in time."
  },
  {
    questionNumber: 68,
    question: "Which factor most directly affects the value of income property?",
    answerOptions: [
      {
        text: "Net operating income",
        rationale: "NOI determines the property's ability to produce return on investment.",
        isCorrect: true
      },
      {
        text: "Gross scheduled rent",
        rationale: "Doesn't account for vacancies or expenses.",
        isCorrect: false
      },
      {
        text: "Original construction cost",
        rationale: "Past cost rarely affects income valuation.",
        isCorrect: false
      },
      {
        text: "Replacement cost new",
        rationale: "Cost approach is secondary for income properties.",
        isCorrect: false
      }
    ],
    hint: "Income property = income performance."
  },
  {
    questionNumber: 69,
    question: "The principle of conformity suggests that:",
    answerOptions: [
      {
        text: "Maximum value is achieved when properties are similar in style and use",
        rationale: "Uniformity maintains neighborhood desirability and value.",
        isCorrect: true
      },
      {
        text: "Property values always increase together",
        rationale: "Not necessarily; conformity influences relative stability.",
        isCorrect: false
      },
      {
        text: "Diverse property uses enhance value",
        rationale: "Opposite of conformity principle.",
        isCorrect: false
      },
      {
        text: "Unique design guarantees highest price",
        rationale: "Uniqueness can hurt value if inconsistent with surroundings.",
        isCorrect: false
      }
    ],
    hint: "Sameness breeds stability."
  },
  {
    questionNumber: 70,
    question: "An appraiser must be licensed or certified by:",
    answerOptions: [
      {
        text: "The California Bureau of Real Estate Appraisers (BREA)",
        rationale: "BREA regulates licensing for all appraisers in the state.",
        isCorrect: true
      },
      {
        text: "The Department of Real Estate (DRE)",
        rationale: "DRE licenses agents and brokers, not appraisers.",
        isCorrect: false
      },
      {
        text: "The Federal Housing Administration (FHA)",
        rationale: "FHA approves appraisers for its programs but does not license them.",
        isCorrect: false
      },
      {
        text: "The Appraisal Institute",
        rationale: "Professional organization; does not issue state licenses.",
        isCorrect: false
      }
    ],
    hint: "BREA = California's appraiser regulator."
  },
  {
    questionNumber: 71,
    question: "A promissory note is best described as:",
    answerOptions: [
      {
        text: "Evidence of a debt and the borrower's personal promise to repay",
        rationale: "The note is the actual contract between borrower and lender.",
        isCorrect: true
      },
      {
        text: "The security instrument for the loan",
        rationale: "That describes a trust deed or mortgage, not the note.",
        isCorrect: false
      },
      {
        text: "A conditional receipt for earnest money",
        rationale: "Unrelated to financing; applies to purchase offers.",
        isCorrect: false
      },
      {
        text: "Title document proving ownership",
        rationale: "Notes do not convey title.",
        isCorrect: false
      }
    ],
    hint: "Think: the IOU that creates the debt."
  },
  {
    questionNumber: 72,
    question: "The trustor in a deed of trust is the:",
    answerOptions: [
      {
        text: "Borrower",
        rationale: "The borrower (trustor) conveys bare legal title to the trustee for security.",
        isCorrect: true
      },
      {
        text: "Lender",
        rationale: "The lender is the beneficiary under the deed of trust.",
        isCorrect: false
      },
      {
        text: "Neutral third party",
        rationale: "That's the trustee.",
        isCorrect: false
      },
      {
        text: "Escrow officer",
        rationale: "Escrow handles closing but is not part of the trust deed arrangement.",
        isCorrect: false
      }
    ],
    hint: "'O' in trustor = the one who owes."
  },
  {
    questionNumber: 73,
    question: "In California, a deed of trust differs from a mortgage because:",
    answerOptions: [
      {
        text: "It involves a third-party trustee who can sell the property nonjudicially",
        rationale: "This allows quicker foreclosure without court action.",
        isCorrect: true
      },
      {
        text: "It conveys full ownership to the lender",
        rationale: "The lender holds only beneficial interest, not title.",
        isCorrect: false
      },
      {
        text: "It cannot be used for residential property",
        rationale: "Trust deeds are standard for California residential loans.",
        isCorrect: false
      },
      {
        text: "It requires judicial foreclosure",
        rationale: "That applies to mortgages, not deeds of trust.",
        isCorrect: false
      }
    ],
    hint: "Trust deed = faster foreclosure route."
  },
  {
    questionNumber: 74,
    question: "The clause in a loan that allows the lender to demand full repayment upon default is called:",
    answerOptions: [
      {
        text: "Acceleration clause",
        rationale: "It accelerates the due date of the entire loan after default.",
        isCorrect: true
      },
      {
        text: "Alienation clause",
        rationale: "Requires payoff upon sale or transfer of property.",
        isCorrect: false
      },
      {
        text: "Prepayment clause",
        rationale: "Relates to paying early, not default.",
        isCorrect: false
      },
      {
        text: "Subordination clause",
        rationale: "Changes lien priority, not repayment terms.",
        isCorrect: false
      }
    ],
    hint: "Default → debt accelerates."
  },
  {
    questionNumber: 75,
    question: "A borrower sells property without lender consent, triggering the due-on-sale clause. The lender may:",
    answerOptions: [
      {
        text: "Call the entire loan balance due immediately",
        rationale: "Due-on-sale (alienation) clauses protect lenders from unapproved assumptions.",
        isCorrect: true
      },
      {
        text: "Increase the interest rate only",
        rationale: "They can demand full payoff, not modify the rate.",
        isCorrect: false
      },
      {
        text: "Reduce the loan term",
        rationale: "No authority to alter term unilaterally.",
        isCorrect: false
      },
      {
        text: "Allow transfer automatically",
        rationale: "Requires written consent to permit assumption.",
        isCorrect: false
      }
    ],
    hint: "'Alienation' = transfer → loan becomes due."
  },
  {
    questionNumber: 76,
    question: "Discount points are charged by lenders to:",
    answerOptions: [
      {
        text: "Increase the lender's effective yield or lower the borrower's interest rate",
        rationale: "Points represent prepaid interest and adjust the loan's yield.",
        isCorrect: true
      },
      {
        text: "Pay for appraisal and title insurance",
        rationale: "Those are closing costs, not discount points.",
        isCorrect: false
      },
      {
        text: "Reduce the loan principal",
        rationale: "Points do not reduce principal; they affect interest cost.",
        isCorrect: false
      },
      {
        text: "Cover property taxes in advance",
        rationale: "Taxes are separate impound items.",
        isCorrect: false
      }
    ],
    hint: "Think 'points buy down rate or raise yield.'"
  },
  {
    questionNumber: 77,
    question: "A borrower's equity in property is defined as:",
    answerOptions: [
      {
        text: "Market value minus outstanding loan balance",
        rationale: "Equity is the owner's interest after debts are subtracted.",
        isCorrect: true
      },
      {
        text: "Total loan balance plus improvements",
        rationale: "That's unrelated to ownership interest.",
        isCorrect: false
      },
      {
        text: "Purchase price minus closing costs",
        rationale: "Closing costs do not define equity.",
        isCorrect: false
      },
      {
        text: "Down payment only",
        rationale: "Initial equity equals down payment but changes with appreciation or debt reduction.",
        isCorrect: false
      }
    ],
    hint: "Equity = what the owner truly owns."
  },
  {
    questionNumber: 78,
    question: "The FHA primarily:",
    answerOptions: [
      {
        text: "Insures lenders against losses on approved loans",
        rationale: "FHA does not make loans; it provides mortgage insurance.",
        isCorrect: true
      },
      {
        text: "Lends money directly to homebuyers",
        rationale: "That's done by private lenders.",
        isCorrect: false
      },
      {
        text: "Guarantees loans like the VA",
        rationale: "FHA insures; VA guarantees.",
        isCorrect: false
      },
      {
        text: "Regulates interest rates charged by lenders",
        rationale: "Market determines rates.",
        isCorrect: false
      }
    ],
    hint: "Insure = FHA; Guarantee = VA."
  },
  {
    questionNumber: 79,
    question: "The VA loan program is available to:",
    answerOptions: [
      {
        text: "Eligible veterans and certain active-duty service members",
        rationale: "VA loans are a benefit for military members and veterans.",
        isCorrect: true
      },
      {
        text: "All U.S. citizens purchasing primary residences",
        rationale: "Restricted to qualified military service members.",
        isCorrect: false
      },
      {
        text: "Any buyer of government-owned homes",
        rationale: "Unrelated to ownership status.",
        isCorrect: false
      },
      {
        text: "Low-income first-time buyers only",
        rationale: "Qualification is based on service, not income or first-time status.",
        isCorrect: false
      }
    ],
    hint: "Veterans Administration = for those who served."
  },
  {
    questionNumber: 80,
    question: "Private mortgage insurance (PMI) is typically required when:",
    answerOptions: [
      {
        text: "The loan-to-value ratio exceeds 80%",
        rationale: "PMI protects the lender when borrowers put down less than 20%.",
        isCorrect: true
      },
      {
        text: "The borrower has a VA-guaranteed loan",
        rationale: "VA loans require no PMI.",
        isCorrect: false
      },
      {
        text: "The borrower's credit score is under 700",
        rationale: "PMI is based on down payment, not credit score.",
        isCorrect: false
      },
      {
        text: "The property is non-owner-occupied",
        rationale: "Occupancy affects rates, not PMI rules.",
        isCorrect: false
      }
    ],
    hint: "80% LTV = PMI threshold."
  },
  {
    questionNumber: 81,
    question: "Under the Truth-in-Lending Act (Regulation Z), lenders must disclose:",
    answerOptions: [
      {
        text: "The annual percentage rate (APR)",
        rationale: "APR expresses the total cost of credit as a yearly rate.",
        isCorrect: true
      },
      {
        text: "The property's assessed value",
        rationale: "That's unrelated to credit disclosure.",
        isCorrect: false
      },
      {
        text: "The borrower's income",
        rationale: "Provided by the borrower, not disclosed by lender.",
        isCorrect: false
      },
      {
        text: "Title insurance coverage limits",
        rationale: "Not part of TILA requirements.",
        isCorrect: false
      }
    ],
    hint: "Reg Z = cost of borrowing transparency."
  },
  {
    questionNumber: 82,
    question: "The Real Estate Settlement Procedures Act (RESPA) applies to:",
    answerOptions: [
      {
        text: "Federally related mortgage loans for 1-to-4 family residential properties",
        rationale: "RESPA covers most residential loans to prevent kickbacks and mandate disclosures.",
        isCorrect: true
      },
      {
        text: "All commercial loans nationwide",
        rationale: "RESPA does not cover commercial transactions.",
        isCorrect: false
      },
      {
        text: "Cash purchases with no financing",
        rationale: "RESPA applies only to financed deals.",
        isCorrect: false
      },
      {
        text: "Seller-financed transactions only",
        rationale: "Applies mainly to institutional lending, not private carrybacks.",
        isCorrect: false
      }
    ],
    hint: "RESPA = residential + federally related loan."
  },
  {
    questionNumber: 83,
    question: "Under RESPA, a lender must provide the Loan Estimate form:",
    answerOptions: [
      {
        text: "Within three business days of receiving a loan application",
        rationale: "The rule ensures timely disclosure of loan costs to borrowers.",
        isCorrect: true
      },
      {
        text: "At closing",
        rationale: "That's when the Closing Disclosure is provided, not the Loan Estimate.",
        isCorrect: false
      },
      {
        text: "Before issuing preapproval",
        rationale: "Disclosure is triggered by a formal application, not preapproval.",
        isCorrect: false
      },
      {
        text: "Within seven days of recording the deed",
        rationale: "Recording occurs after closing.",
        isCorrect: false
      }
    ],
    hint: "'Three-day rule' for Loan Estimate under RESPA."
  },
  {
    questionNumber: 84,
    question: "A mortgage that includes both real and personal property is called a:",
    answerOptions: [
      {
        text: "Package mortgage",
        rationale: "Covers both real estate and personal property such as appliances or furnishings.",
        isCorrect: true
      },
      {
        text: "Blanket mortgage",
        rationale: "Covers multiple parcels of real estate.",
        isCorrect: false
      },
      {
        text: "Open-end mortgage",
        rationale: "Allows additional borrowing on the same property.",
        isCorrect: false
      },
      {
        text: "Shared-appreciation mortgage",
        rationale: "Involves lender sharing in appreciation, not collateral mix.",
        isCorrect: false
      }
    ],
    hint: "Think: real + personal = package deal."
  },
  {
    questionNumber: 85,
    question: "When the lender releases the deed of trust after full repayment, the document recorded is a:",
    answerOptions: [
      {
        text: "Reconveyance deed",
        rationale: "The trustee reconveys title to the borrower once the loan is paid.",
        isCorrect: true
      },
      {
        text: "Quitclaim deed",
        rationale: "Used to clear title, not release a loan.",
        isCorrect: false
      },
      {
        text: "Grant deed",
        rationale: "Used to transfer ownership, not reconvey security title.",
        isCorrect: false
      },
      {
        text: "Warranty deed",
        rationale: "Not common in California, and unrelated to loan payoff.",
        isCorrect: false
      }
    ],
    hint: "'Re-convey' = give back title after payoff."
  },
  {
    questionNumber: 86,
    question: "Title to real property in California is transferred when:",
    answerOptions: [
      {
        text: "The deed is delivered and accepted by the grantee",
        rationale: "Delivery and acceptance, not recording, complete the transfer.",
        isCorrect: true
      },
      {
        text: "The deed is signed and notarized",
        rationale: "Signing alone doesn't transfer title without delivery.",
        isCorrect: false
      },
      {
        text: "The deed is recorded at the county recorder's office",
        rationale: "Recording gives constructive notice but is not required for validity.",
        isCorrect: false
      },
      {
        text: "Escrow closes and funds are disbursed",
        rationale: "Escrow coordinates transfer, but title passes by delivery and acceptance.",
        isCorrect: false
      }
    ],
    hint: "Recording is not essential; delivery and acceptance are."
  },
  {
    questionNumber: 87,
    question: "A valid deed must contain:",
    answerOptions: [
      {
        text: "A granting clause and identification of the grantee",
        rationale: "These elements are essential for conveyance.",
        isCorrect: true
      },
      {
        text: "The amount of consideration in dollars",
        rationale: "'For valuable consideration' is sufficient.",
        isCorrect: false
      },
      {
        text: "A signature of the grantee",
        rationale: "Only the grantor signs a deed.",
        isCorrect: false
      },
      {
        text: "An acknowledgment before delivery",
        rationale: "Acknowledgment is required for recording, not validity.",
        isCorrect: false
      }
    ],
    hint: "Think: grantor's signature, granting clause, identifiable grantee."
  },
  {
    questionNumber: 88,
    question: "A quitclaim deed provides:",
    answerOptions: [
      {
        text: "No warranties of title, only transfer of whatever interest the grantor has",
        rationale: "It conveys any interest the grantor may hold, without guarantee.",
        isCorrect: true
      },
      {
        text: "Full covenants of seisin and warranty forever",
        rationale: "That describes a warranty deed, not used in California.",
        isCorrect: false
      },
      {
        text: "Implied guarantee of marketable title",
        rationale: "Quitclaim deeds contain no such guarantee.",
        isCorrect: false
      },
      {
        text: "A security interest for the lender",
        rationale: "That would be a trust deed.",
        isCorrect: false
      }
    ],
    hint: "Quitclaim = no promises — just 'whatever I own, if anything.'"
  },
  {
    questionNumber: 89,
    question: "Which of the following deeds is most commonly used to transfer ownership in California?",
    answerOptions: [
      {
        text: "Grant deed",
        rationale: "Grant deeds include implied warranties of ownership and no prior conveyances.",
        isCorrect: true
      },
      {
        text: "Warranty deed",
        rationale: "Rarely used in California.",
        isCorrect: false
      },
      {
        text: "Bargain and sale deed",
        rationale: "Less common; provides limited assurances.",
        isCorrect: false
      },
      {
        text: "Quitclaim deed",
        rationale: "Used for clearing title or family transfers, not standard sales.",
        isCorrect: false
      }
    ],
    hint: "California = Grant deed state."
  },
  {
    questionNumber: 90,
    question: "Recording a deed provides:",
    answerOptions: [
      {
        text: "Constructive notice to the public of ownership",
        rationale: "Recording informs the world even if no one actually sees the document.",
        isCorrect: true
      },
      {
        text: "Actual notice to the buyer only",
        rationale: "Actual notice means direct knowledge.",
        isCorrect: false
      },
      {
        text: "Proof that title is valid",
        rationale: "Recording doesn't guarantee validity, only priority.",
        isCorrect: false
      },
      {
        text: "Evidence of title insurance coverage",
        rationale: "Title insurance is a separate protection.",
        isCorrect: false
      }
    ],
    hint: "Recording = public notice = constructive notice."
  },
  {
    questionNumber: 91,
    question: "Escrow acts as:",
    answerOptions: [
      {
        text: "A neutral third party to carry out the terms of the purchase agreement",
        rationale: "Escrow ensures all instructions are performed before closing.",
        isCorrect: true
      },
      {
        text: "An agent for the seller only",
        rationale: "Escrow is neutral and serves both parties.",
        isCorrect: false
      },
      {
        text: "An advocate for the lender",
        rationale: "Escrow is not a fiduciary for any party.",
        isCorrect: false
      },
      {
        text: "A representative of the title company",
        rationale: "Escrow and title are separate entities, though they cooperate.",
        isCorrect: false
      }
    ],
    hint: "Neutral = neither side's agent."
  },
  {
    questionNumber: 92,
    question: "During escrow, if the buyer and seller disagree about the disbursement of funds, the escrow officer should:",
    answerOptions: [
      {
        text: "Hold funds until the dispute is resolved or a court order is obtained",
        rationale: "Escrow must remain neutral and not release disputed funds.",
        isCorrect: true
      },
      {
        text: "Give the funds to the seller",
        rationale: "That would violate neutrality and escrow law.",
        isCorrect: false
      },
      {
        text: "Return all funds to the buyer immediately",
        rationale: "Escrow cannot act unilaterally.",
        isCorrect: false
      },
      {
        text: "Release the funds to the broker",
        rationale: "Brokers cannot receive escrow funds unless authorized.",
        isCorrect: false
      }
    ],
    hint: "Escrow = hold money until agreement or judgment."
  },
  {
    questionNumber: 93,
    question: "Title insurance protects against:",
    answerOptions: [
      {
        text: "Losses due to defects in title existing at the time of issuance",
        rationale: "It covers past events, not future risks.",
        isCorrect: true
      },
      {
        text: "Zoning changes after purchase",
        rationale: "Title insurance doesn't cover government actions or future laws.",
        isCorrect: false
      },
      {
        text: "Physical damage to improvements",
        rationale: "That's property hazard insurance.",
        isCorrect: false
      },
      {
        text: "Market value decline",
        rationale: "Title insurance doesn't guarantee value.",
        isCorrect: false
      }
    ],
    hint: "Title insurance looks backward, not forward."
  },
  {
    questionNumber: 94,
    question: "The standard coverage title insurance policy (CLTA) protects against:",
    answerOptions: [
      {
        text: "Recorded liens and encumbrances not disclosed in the policy",
        rationale: "Standard coverage includes matters of public record.",
        isCorrect: true
      },
      {
        text: "Rights of parties in possession",
        rationale: "That requires extended (ALTA) coverage.",
        isCorrect: false
      },
      {
        text: "Survey errors or encroachments",
        rationale: "Not covered under standard policy.",
        isCorrect: false
      },
      {
        text: "Zoning violations",
        rationale: "Excluded from all title policies.",
        isCorrect: false
      }
    ],
    hint: "CLTA = 'recorded matters only.'"
  },
  {
    questionNumber: 95,
    question: "The documentary transfer tax is based on:",
    answerOptions: [
      {
        text: "The consideration or purchase price of the property conveyed",
        rationale: "It's usually $1.10 per $1,000 of value in most California counties.",
        isCorrect: true
      },
      {
        text: "The property's assessed value for taxes",
        rationale: "Assessment and sale price often differ.",
        isCorrect: false
      },
      {
        text: "The loan amount financed",
        rationale: "Loans are unrelated to transfer tax calculations.",
        isCorrect: false
      },
      {
        text: "The equity amount at closing",
        rationale: "Transfer tax is based on total consideration, not equity.",
        isCorrect: false
      }
    ],
    hint: "Think: a small percentage of the price paid."
  },
  {
    questionNumber: 96,
    question: "Property taxes in California are based on:",
    answerOptions: [
      {
        text: "1% of the property's assessed value, plus voter-approved bonds",
        rationale: "Under Proposition 13, base property tax is 1% plus local assessments.",
        isCorrect: true
      },
      {
        text: "A progressive rate based on income level",
        rationale: "Property tax is based on value, not income.",
        isCorrect: false
      },
      {
        text: "The property's market value every year",
        rationale: "Assessed value is limited to 2% annual increases unless sold or improved.",
        isCorrect: false
      },
      {
        text: "The owner's equity",
        rationale: "Equity doesn't affect tax base.",
        isCorrect: false
      }
    ],
    hint: "Prop 13: 1% + local bonds = tax bill."
  },
  {
    questionNumber: 97,
    question: "Property tax liens attach to property on:",
    answerOptions: [
      {
        text: "January 1 each year",
        rationale: "That's the lien date for the fiscal year beginning July 1.",
        isCorrect: true
      },
      {
        text: "April 10",
        rationale: "That's the second installment delinquency date.",
        isCorrect: false
      },
      {
        text: "July 1",
        rationale: "Fiscal year start, not lien date.",
        isCorrect: false
      },
      {
        text: "November 1",
        rationale: "First installment due date.",
        isCorrect: false
      }
    ],
    hint: "Tax lien attaches at the start of the calendar year."
  },
  {
    questionNumber: 98,
    question: "The primary purpose of the California Department of Real Estate (DRE) is to:",
    answerOptions: [
      {
        text: "Protect the public through licensing and regulation of real estate professionals",
        rationale: "The DRE's mission is public protection, not industry promotion.",
        isCorrect: true
      },
      {
        text: "Promote the interests of licensed brokers",
        rationale: "That would create bias against consumers.",
        isCorrect: false
      },
      {
        text: "Set property tax rates statewide",
        rationale: "That's done by counties under Proposition 13.",
        isCorrect: false
      },
      {
        text: "Determine property values for lenders",
        rationale: "Appraisers and lenders handle valuation, not the DRE.",
        isCorrect: false
      }
    ],
    hint: "DRE = consumer protection agency."
  },
  {
    questionNumber: 99,
    question: "A real estate license is required to:",
    answerOptions: [
      {
        text: "Negotiate the sale or lease of another's property for compensation",
        rationale: "Performing these acts for pay requires a license under California law.",
        isCorrect: true
      },
      {
        text: "Buy or sell one's own property",
        rationale: "Individuals may sell their own property without a license.",
        isCorrect: false
      },
      {
        text: "Serve as an on-site apartment manager receiving salary only",
        rationale: "On-site salaried managers are exempt.",
        isCorrect: false
      },
      {
        text: "Collect rent as an employee of a broker",
        rationale: "Employees of brokers act under the broker's license.",
        isCorrect: false
      }
    ],
    hint: "'For another and for compensation' = license required."
  },
  {
    questionNumber: 100,
    question: "An unlicensed assistant may do which of the following?",
    answerOptions: [
      {
        text: "Prepare flyers and perform clerical duties under broker supervision",
        rationale: "Administrative tasks are permitted if no negotiation or showing occurs.",
        isCorrect: true
      },
      {
        text: "Host open houses for clients",
        rationale: "Only licensees may conduct open houses.",
        isCorrect: false
      },
      {
        text: "Negotiate lease terms with tenants",
        rationale: "Negotiation requires a license.",
        isCorrect: false
      },
      {
        text: "Discuss property features with potential buyers",
        rationale: "That constitutes licensed activity.",
        isCorrect: false
      }
    ],
    hint: "Administrative = OK; negotiation = not OK."
  },
  {
    questionNumber: 101,
    question: "The Real Estate Commissioner may suspend or revoke a license for:",
    answerOptions: [
      {
        text: "Commingling or conversion of trust funds",
        rationale: "Misuse of client funds is a major violation.",
        isCorrect: true
      },
      {
        text: "Working exclusively with buyers",
        rationale: "That's allowed; not misconduct.",
        isCorrect: false
      },
      {
        text: "Earning a high commission",
        rationale: "High commission alone is not illegal.",
        isCorrect: false
      },
      {
        text: "Referring clients to lenders",
        rationale: "Permitted if disclosed and no illegal kickbacks occur.",
        isCorrect: false
      }
    ],
    hint: "Trust-fund mishandling = automatic discipline."
  },
  {
    questionNumber: 102,
    question: "A broker must retain copies of all listings, deposits, and related documents for at least:",
    answerOptions: [
      {
        text: "Three years",
        rationale: "Required under the California Business and Professions Code § 10148.",
        isCorrect: true
      },
      {
        text: "One year",
        rationale: "Too short; violates record-keeping law.",
        isCorrect: false
      },
      {
        text: "Five years",
        rationale: "Longer than the statutory requirement.",
        isCorrect: false
      },
      {
        text: "Seven years",
        rationale: "IRS audit rules differ; DRE requires three.",
        isCorrect: false
      }
    ],
    hint: "Remember the 3-year DRE rule."
  },
  {
    questionNumber: 103,
    question: "Blockbusting refers to:",
    answerOptions: [
      {
        text: "Inducing owners to sell by suggesting that protected groups are moving into the neighborhood",
        rationale: "This discriminatory practice violates fair-housing laws.",
        isCorrect: true
      },
      {
        text: "Refusing to rent to families with children",
        rationale: "That's discrimination based on familial status, not blockbusting.",
        isCorrect: false
      },
      {
        text: "Directing buyers to certain areas based on race",
        rationale: "That's steering.",
        isCorrect: false
      },
      {
        text: "Charging higher rents to certain ethnic groups",
        rationale: "That's price discrimination, not blockbusting.",
        isCorrect: false
      }
    ],
    hint: "Blockbusting = fear-selling."
  },
  {
    questionNumber: 104,
    question: "The Civil Rights Act of 1968 (Fair Housing Act) prohibits discrimination in housing based on:",
    answerOptions: [
      {
        text: "Race, color, religion, sex, national origin, disability, and familial status",
        rationale: "These are the seven federal protected classes.",
        isCorrect: true
      },
      {
        text: "Marital status and age",
        rationale: "Those are state-protected under California law, not federal.",
        isCorrect: false
      },
      {
        text: "Political affiliation and source of income",
        rationale: "Also state or local protections, not federal.",
        isCorrect: false
      },
      {
        text: "Occupation and credit history",
        rationale: "Not protected under the federal Act.",
        isCorrect: false
      }
    ],
    hint: "Think: federal seven protected classes."
  },
  {
    questionNumber: 105,
    question: "Steering occurs when an agent:",
    answerOptions: [
      {
        text: "Directs buyers to or away from certain areas based on protected characteristics",
        rationale: "Steering violates both state and federal fair-housing laws.",
        isCorrect: true
      },
      {
        text: "Shows buyers only properties within their price range",
        rationale: "That's legitimate business practice.",
        isCorrect: false
      },
      {
        text: "Advertises homes near schools",
        rationale: "Neutral advertising is allowed.",
        isCorrect: false
      },
      {
        text: "Explains neighborhood demographics factually when asked",
        rationale: "Factual, data-based answers are legal.",
        isCorrect: false
      }
    ],
    hint: "Steering = directing by bias."
  },
  {
    questionNumber: 106,
    question: "Which federal law requires lenders to disclose known environmental hazards such as lead paint in pre-1978 housing?",
    answerOptions: [
      {
        text: "Residential Lead-Based Paint Hazard Reduction Act",
        rationale: "Also called Title X of 1992, it mandates disclosure to buyers and tenants.",
        isCorrect: true
      },
      {
        text: "Clean Air Act",
        rationale: "Focuses on air-quality emissions.",
        isCorrect: false
      },
      {
        text: "CERCLA (Superfund)",
        rationale: "Addresses hazardous-waste cleanup, not disclosure.",
        isCorrect: false
      },
      {
        text: "Safe Drinking Water Act",
        rationale: "Regulates water quality, not housing disclosures.",
        isCorrect: false
      }
    ],
    hint: "Pre-1978 = lead-paint disclosure rule."
  },
  {
    questionNumber: 107,
    question: "Which of the following must always be disclosed by a seller or agent?",
    answerOptions: [
      {
        text: "Material facts affecting the property's value or desirability",
        rationale: "Even if not asked, material facts must be revealed.",
        isCorrect: true
      },
      {
        text: "Personal reasons for selling",
        rationale: "Private motives need not be disclosed.",
        isCorrect: false
      },
      {
        text: "Owner's profit margin",
        rationale: "Financial details are confidential.",
        isCorrect: false
      },
      {
        text: "Minor cosmetic issues like faded paint",
        rationale: "Only material defects require disclosure.",
        isCorrect: false
      }
    ],
    hint: "If it would affect a buyer's decision, disclose it."
  },
  {
    questionNumber: 108,
    question: "The Transfer Disclosure Statement (TDS) is required in most residential sales to be completed by:",
    answerOptions: [
      {
        text: "The seller",
        rationale: "The seller must complete the TDS to disclose known defects.",
        isCorrect: true
      },
      {
        text: "The listing agent",
        rationale: "Agents review for completeness but do not fill it out.",
        isCorrect: false
      },
      {
        text: "The buyer's agent",
        rationale: "Buyers do not prepare the form.",
        isCorrect: false
      },
      {
        text: "The escrow officer",
        rationale: "Escrow handles documents but not disclosures.",
        isCorrect: false
      }
    ],
    hint: "Seller fills, agents verify."
  },
  {
    questionNumber: 109,
    question: "An agent who suspects a roof leak but fails to investigate or disclose could be guilty of:",
    answerOptions: [
      {
        text: "Negligent misrepresentation",
        rationale: "Failure to disclose known or observable defects constitutes negligence.",
        isCorrect: true
      },
      {
        text: "Fraud by omission",
        rationale: "Fraud requires intent; this scenario is negligence.",
        isCorrect: false
      },
      {
        text: "Innocent misrepresentation",
        rationale: "The agent ignored signs, not innocent.",
        isCorrect: false
      },
      {
        text: "Conversion",
        rationale: "Conversion deals with money, not misrepresentation.",
        isCorrect: false
      }
    ],
    hint: "Negligent = careless failure to disclose."
  },
  {
    questionNumber: 110,
    question: "Which environmental hazard requires an agency disclosure booklet under California law?",
    answerOptions: [
      {
        text: "Mold and moisture intrusion",
        rationale: "California Health & Safety Code mandates mold disclosure when known.",
        isCorrect: true
      },
      {
        text: "Carbon monoxide",
        rationale: "Detectors required, but no booklet disclosure.",
        isCorrect: false
      },
      {
        text: "High-voltage power lines",
        rationale: "Not a mandatory disclosure booklet item.",
        isCorrect: false
      },
      {
        text: "Noise from nearby highways",
        rationale: "Material if extreme but not booklet-mandated.",
        isCorrect: false
      }
    ],
    hint: "California includes mold booklet along with lead-based paint."
  },
  {
    questionNumber: 111,
    question: "The Natural Hazard Disclosure (NHD) statement covers which of the following?",
    answerOptions: [
      {
        text: "Flood, fire, seismic, and other hazard zones",
        rationale: "NHD requires disclosure of state-mapped natural hazard zones.",
        isCorrect: true
      },
      {
        text: "Radon gas levels inside the home",
        rationale: "Not specifically part of NHD zones.",
        isCorrect: false
      },
      {
        text: "Presence of asbestos insulation",
        rationale: "Separate environmental issue, not NHD.",
        isCorrect: false
      },
      {
        text: "Crime statistics in the area",
        rationale: "Not a natural hazard disclosure item.",
        isCorrect: false
      }
    ],
    hint: "Floods, fires, faults — the big three F's."
  },
  {
    questionNumber: 112,
    question: "Failure to provide a Transfer Disclosure Statement allows the buyer to:",
    answerOptions: [
      {
        text: "Cancel the transaction before closing",
        rationale: "Buyer may rescind if TDS not delivered before close of escrow.",
        isCorrect: true
      },
      {
        text: "Sue the listing agent for triple damages automatically",
        rationale: "Damages depend on proof of harm, not automatic.",
        isCorrect: false
      },
      {
        text: "Delay closing indefinitely",
        rationale: "They must act promptly to rescind.",
        isCorrect: false
      },
      {
        text: "Force the seller to reduce price",
        rationale: "Price changes aren't required by law.",
        isCorrect: false
      }
    ],
    hint: "Missing TDS = rescission right."
  },
  {
    questionNumber: 113,
    question: "Puffing by an agent is:",
    answerOptions: [
      {
        text: "An exaggerated opinion not intended as a factual statement",
        rationale: "'Best view in town!' is puffing, not misrepresentation.",
        isCorrect: true
      },
      {
        text: "Illegal under California law",
        rationale: "Allowed if clearly opinion, not fact.",
        isCorrect: false
      },
      {
        text: "A form of fraud",
        rationale: "Fraud requires false factual representation.",
        isCorrect: false
      },
      {
        text: "A violation of antitrust laws",
        rationale: "Antitrust deals with competition, not sales talk.",
        isCorrect: false
      }
    ],
    hint: "Puffing = sales talk, not lying."
  },
  {
    questionNumber: 114,
    question: "Which of the following actions would violate antitrust laws?",
    answerOptions: [
      {
        text: "Two brokers agreeing to fix commission rates",
        rationale: "Price-fixing is an antitrust violation under the Sherman Act.",
        isCorrect: true
      },
      {
        text: "Brokers setting their own independent fees",
        rationale: "Independent pricing is lawful.",
        isCorrect: false
      },
      {
        text: "Offering discounts to repeat clients",
        rationale: "Allowed and often encouraged.",
        isCorrect: false
      },
      {
        text: "Refusing to cooperate with another broker for business reasons",
        rationale: "May be unwise but not antitrust unless there's collusion.",
        isCorrect: false
      }
    ],
    hint: "Agreements between competitors = red flag."
  },
  {
    questionNumber: 115,
    question: "A broker who represents both the buyer and seller in the same transaction is practicing:",
    answerOptions: [
      {
        text: "Dual agency",
        rationale: "Dual agency requires written consent from both parties.",
        isCorrect: true
      },
      {
        text: "Designated agency",
        rationale: "Not recognized under California law.",
        isCorrect: false
      },
      {
        text: "Implied agency",
        rationale: "Created by actions, not by dual representation.",
        isCorrect: false
      },
      {
        text: "Subagency",
        rationale: "Subagent represents the seller through another broker.",
        isCorrect: false
      }
    ],
    hint: "Both sides = dual; get written consent."
  },
  {
    questionNumber: 116,
    question: "When must the Agency Disclosure form be presented to a seller in a residential listing?",
    answerOptions: [
      {
        text: "Before or at the time the listing is signed",
        rationale: "California law requires disclosure before the agency relationship begins.",
        isCorrect: true
      },
      {
        text: "After an offer is accepted",
        rationale: "Too late; agency already exists.",
        isCorrect: false
      },
      {
        text: "At the close of escrow",
        rationale: "Disclosure must occur upfront.",
        isCorrect: false
      },
      {
        text: "Only if the seller requests it",
        rationale: "It's mandatory, not optional.",
        isCorrect: false
      }
    ],
    hint: "Agency disclosure = before commitment."
  },
  {
    questionNumber: 117,
    question: "Trust fund records must be reconciled:",
    answerOptions: [
      {
        text: "At least once each month",
        rationale: "Monthly reconciliation is required under DRE regulations.",
        isCorrect: true
      },
      {
        text: "Quarterly",
        rationale: "Too infrequent for compliance.",
        isCorrect: false
      },
      {
        text: "Weekly",
        rationale: "Allowed but not mandated.",
        isCorrect: false
      },
      {
        text: "Only when requested by DRE auditors",
        rationale: "Broker responsibility, not DRE-triggered.",
        isCorrect: false
      }
    ],
    hint: "Monthly trust account check-up required."
  },
  {
    questionNumber: 118,
    question: "A real estate broker advertising a property must:",
    answerOptions: [
      {
        text: "Include the broker's license identification number in all first point-of-contact materials",
        rationale: "Required by California Business and Professions Code §10140.6.",
        isCorrect: true
      },
      {
        text: "Display the property's assessed value",
        rationale: "Not required and may mislead buyers.",
        isCorrect: false
      },
      {
        text: "Obtain DRE preapproval for all advertisements",
        rationale: "No preapproval process exists.",
        isCorrect: false
      },
      {
        text: "List all open-house hours in every ad",
        rationale: "Optional, not mandatory.",
        isCorrect: false
      }
    ],
    hint: "DRE number = must appear on ads."
  },
  {
    questionNumber: 119,
    question: "If a real estate license expires, the licensee may:",
    answerOptions: [
      {
        text: "Renew within two years of expiration without reexamination",
        rationale: "After two years, reexamination is required.",
        isCorrect: true
      },
      {
        text: "Continue practicing while renewal is pending",
        rationale: "No — cannot practice with an expired license.",
        isCorrect: false
      },
      {
        text: "Reapply anytime without penalty",
        rationale: "Late renewals incur fees and deadlines.",
        isCorrect: false
      },
      {
        text: "Operate under another broker's license",
        rationale: "Illegal — each license is personal.",
        isCorrect: false
      }
    ],
    hint: "2-year grace period (no reexam)."
  },
  {
    questionNumber: 120,
    question: "The DRE may issue a restricted license when:",
    answerOptions: [
      {
        text: "The licensee has been disciplined and conditions must be imposed",
        rationale: "Restricted licenses allow continued practice under supervision or limits.",
        isCorrect: true
      },
      {
        text: "A new salesperson applies for the first time",
        rationale: "New licenses are not restricted unless disciplinary issues exist.",
        isCorrect: false
      },
      {
        text: "A broker requests fewer duties",
        rationale: "Restrictions come from the DRE, not voluntary limitation.",
        isCorrect: false
      },
      {
        text: "The licensee changes brokerage firms",
        rationale: "That doesn't trigger restriction.",
        isCorrect: false
      }
    ],
    hint: "Restriction = disciplinary tool."
  },
  {
    questionNumber: 121,
    question: "A real estate agent must give the seller the 'Homeowner's Guide to Earthquake Safety' when:",
    answerOptions: [
      {
        text: "The property was built before 1960 and is a single-family home",
        rationale: "State law requires the booklet for pre-1960 homes with wood frames.",
        isCorrect: true
      },
      {
        text: "Any home in California is sold",
        rationale: "Applies only to certain older single-family homes.",
        isCorrect: false
      },
      {
        text: "The property is in a flood zone",
        rationale: "That's disclosed through NHD, not earthquake booklet.",
        isCorrect: false
      },
      {
        text: "The buyer requests it",
        rationale: "It's mandatory, not request-based.",
        isCorrect: false
      }
    ],
    hint: "Think: pre-1960 + wood-frame = earthquake booklet."
  },
  {
    questionNumber: 122,
    question: "Which statement about Megan's Law is true?",
    answerOptions: [
      {
        text: "Agents must disclose the existence of the state's sex offender database and provide its website link",
        rationale: "Disclosure is required; agents do not disclose individual names or locations.",
        isCorrect: true
      },
      {
        text: "Agents must disclose if a known offender lives nearby",
        rationale: "That's prohibited; direct disclosure violates privacy laws.",
        isCorrect: false
      },
      {
        text: "Megan's Law applies only to properties near schools",
        rationale: "It applies statewide to residential sales and leases.",
        isCorrect: false
      },
      {
        text: "Only brokers must comply, not salespersons",
        rationale: "All licensees must comply equally.",
        isCorrect: false
      }
    ],
    hint: "Disclosure = existence of database, not names."
  },
  {
    questionNumber: 123,
    question: "A valid contract requires all the following EXCEPT:",
    answerOptions: [
      {
        text: "Monetary consideration",
        rationale: "Consideration may be anything of value, not necessarily money.",
        isCorrect: true
      },
      {
        text: "Competent parties",
        rationale: "All parties must be legally capable of contracting.",
        isCorrect: false
      },
      {
        text: "Lawful object",
        rationale: "Contracts must have legal purpose.",
        isCorrect: false
      },
      {
        text: "Mutual consent",
        rationale: "Offer and acceptance form mutual consent.",
        isCorrect: false
      }
    ],
    hint: "Consideration ≠ cash only."
  },
  {
    questionNumber: 124,
    question: "A void contract is one that:",
    answerOptions: [
      {
        text: "Has no legal effect from the beginning",
        rationale: "It's unenforceable as if it never existed.",
        isCorrect: true
      },
      {
        text: "Can be enforced by either party",
        rationale: "That's a valid contract.",
        isCorrect: false
      },
      {
        text: "Is binding but can be voided by one party",
        rationale: "That describes a voidable contract.",
        isCorrect: false
      },
      {
        text: "Is enforceable after performance",
        rationale: "Performance doesn't validate a void contract.",
        isCorrect: false
      }
    ],
    hint: "Void = never existed legally."
  },
  {
    questionNumber: 125,
    question: "A contract entered into under duress is:",
    answerOptions: [
      {
        text: "Voidable by the injured party",
        rationale: "Contracts made under threat or force lack free consent.",
        isCorrect: true
      },
      {
        text: "Void",
        rationale: "Still valid until the coerced party voids it.",
        isCorrect: false
      },
      {
        text: "Valid and binding",
        rationale: "No — consent was not genuine.",
        isCorrect: false
      },
      {
        text: "Unenforceable only by the aggressor",
        rationale: "The coerced party decides to void.",
        isCorrect: false
      }
    ],
    hint: "Coercion = voidable by victim."
  },
  {
    questionNumber: 126,
    question: "Consideration in a contract is:",
    answerOptions: [
      {
        text: "Anything of value exchanged between the parties",
        rationale: "It can be money, a promise, or even forbearance.",
        isCorrect: true
      },
      {
        text: "Limited to cash payment",
        rationale: "Non-monetary consideration is valid.",
        isCorrect: false
      },
      {
        text: "Required only in unilateral contracts",
        rationale: "Applies to all enforceable contracts.",
        isCorrect: false
      },
      {
        text: "The written portion of an agreement",
        rationale: "Writing is form, not consideration.",
        isCorrect: false
      }
    ],
    hint: "Consideration = value given or promised."
  },
  {
    questionNumber: 127,
    question: "A listing agreement is a contract between:",
    answerOptions: [
      {
        text: "A property owner and a broker",
        rationale: "Creates the agency relationship authorizing the broker to market the property.",
        isCorrect: true
      },
      {
        text: "A buyer and a salesperson",
        rationale: "Buyers sign purchase offers, not listings.",
        isCorrect: false
      },
      {
        text: "A broker and the MLS",
        rationale: "MLS membership is separate.",
        isCorrect: false
      },
      {
        text: "Two cooperating brokers",
        rationale: "They cooperate under MLS rules, not via listing contract.",
        isCorrect: false
      }
    ],
    hint: "Listing = owner ↔ broker."
  },
  {
    questionNumber: 128,
    question: "Which type of listing gives a broker the greatest protection for commission?",
    answerOptions: [
      {
        text: "Exclusive right-to-sell",
        rationale: "Broker earns commission regardless of who finds the buyer.",
        isCorrect: true
      },
      {
        text: "Exclusive agency",
        rationale: "Owner can sell without paying a commission.",
        isCorrect: false
      },
      {
        text: "Open listing",
        rationale: "Only the procuring broker is paid.",
        isCorrect: false
      },
      {
        text: "Net listing",
        rationale: "Discouraged and risky for conflicts of interest.",
        isCorrect: false
      }
    ],
    hint: "Guaranteed pay = exclusive right-to-sell."
  },
  {
    questionNumber: 129,
    question: "An open listing allows:",
    answerOptions: [
      {
        text: "Multiple brokers to list the same property, with commission to the one who produces a buyer",
        rationale: "It's non-exclusive and competitive.",
        isCorrect: true
      },
      {
        text: "Only one broker to advertise the property",
        rationale: "That's exclusive agency.",
        isCorrect: false
      },
      {
        text: "The seller to owe commission regardless of who sells",
        rationale: "Applies to exclusive right-to-sell, not open listing.",
        isCorrect: false
      },
      {
        text: "Payment of partial commission to each broker",
        rationale: "Only procuring broker earns it.",
        isCorrect: false
      }
    ],
    hint: "Open = many brokers, one winner."
  },
  {
    questionNumber: 130,
    question: "A net listing is legal in California only if:",
    answerOptions: [
      {
        text: "The broker fully discloses the amount of commission and seller's net proceeds",
        rationale: "Transparency prevents unfair gain; disclosure is mandatory.",
        isCorrect: true
      },
      {
        text: "The seller requests it verbally",
        rationale: "Must be written and disclosed.",
        isCorrect: false
      },
      {
        text: "It's approved by the DRE before use",
        rationale: "No pre-approval system exists.",
        isCorrect: false
      },
      {
        text: "It's used only for income property",
        rationale: "Applies to any property if disclosure occurs.",
        isCorrect: false
      }
    ],
    hint: "Legal but risky — disclose commission clearly."
  },
  {
    questionNumber: 131,
    question: "An offer may be revoked:",
    answerOptions: [
      {
        text: "Any time before it is accepted",
        rationale: "Offers are not binding until acceptance is communicated.",
        isCorrect: true
      },
      {
        text: "After acceptance but before escrow closes",
        rationale: "Too late — contract already formed.",
        isCorrect: false
      },
      {
        text: "Only with mutual consent",
        rationale: "Revocation is unilateral until accepted.",
        isCorrect: false
      },
      {
        text: "Only if in writing",
        rationale: "Oral revocation is valid if communicated.",
        isCorrect: false
      }
    ],
    hint: "You can take it back — until they say yes."
  },
  {
    questionNumber: 132,
    question: "Which of the following terminates an offer?",
    answerOptions: [
      {
        text: "A counteroffer by the offeree",
        rationale: "A counteroffer rejects and replaces the original offer.",
        isCorrect: true
      },
      {
        text: "Silence from the offeree",
        rationale: "Silence doesn't terminate unless time expires.",
        isCorrect: false
      },
      {
        text: "Inquiry about price",
        rationale: "Asking a question doesn't end the offer.",
        isCorrect: false
      },
      {
        text: "Communication delay",
        rationale: "Offer remains open until expired or revoked.",
        isCorrect: false
      }
    ],
    hint: "Counter = rejection + new offer."
  },
  {
    questionNumber: 133,
    question: "A contract signed by a minor is generally:",
    answerOptions: [
      {
        text: "Voidable by the minor",
        rationale: "Minors lack full contractual capacity.",
        isCorrect: true
      },
      {
        text: "Void from the start",
        rationale: "Valid until disaffirmed.",
        isCorrect: false
      },
      {
        text: "Enforceable by both parties",
        rationale: "Only the minor may enforce or cancel.",
        isCorrect: false
      },
      {
        text: "Valid if witnessed",
        rationale: "Witnesses don't cure incapacity.",
        isCorrect: false
      }
    ],
    hint: "Minor = can void, not bound."
  },
  {
    questionNumber: 134,
    question: "A bilateral contract is one in which:",
    answerOptions: [
      {
        text: "Both parties make promises to each other",
        rationale: "Most real estate contracts are bilateral.",
        isCorrect: true
      },
      {
        text: "Only one party makes a promise",
        rationale: "That's a unilateral contract.",
        isCorrect: false
      },
      {
        text: "Performance is optional",
        rationale: "Obligations are mutual.",
        isCorrect: false
      },
      {
        text: "It must be in writing",
        rationale: "Writing is a separate requirement.",
        isCorrect: false
      }
    ],
    hint: "Bi = two promises."
  },
  {
    questionNumber: 135,
    question: "Specific performance is a legal remedy that:",
    answerOptions: [
      {
        text: "Compels a party to perform the contract as agreed",
        rationale: "Often used when money damages are inadequate, such as in real estate sales.",
        isCorrect: true
      },
      {
        text: "Allows the buyer to cancel without penalty",
        rationale: "That's rescission.",
        isCorrect: false
      },
      {
        text: "Awards money for losses",
        rationale: "That's compensatory damages.",
        isCorrect: false
      },
      {
        text: "Terminates a contract immediately",
        rationale: "Opposite of enforcement.",
        isCorrect: false
      }
    ],
    hint: "Court orders you to do what you promised."
  },
  {
    questionNumber: 136,
    question: "Liquidated damages in a residential purchase agreement are typically limited to:",
    answerOptions: [
      {
        text: "No more than 3% of the purchase price if initialed by both parties",
        rationale: "California Civil Code caps liquidated damages at 3% for residential 1-4 units.",
        isCorrect: true
      },
      {
        text: "5% of the sales price",
        rationale: "Exceeds legal maximum.",
        isCorrect: false
      },
      {
        text: "Amount determined by seller after default",
        rationale: "Not allowed; must be agreed in advance.",
        isCorrect: false
      },
      {
        text: "Broker's full commission",
        rationale: "Commission is separate from damages.",
        isCorrect: false
      }
    ],
    hint: "3% cap — both must initial."
  },
  {
    questionNumber: 137,
    question: "The Statute of Frauds requires that certain contracts be:",
    answerOptions: [
      {
        text: "In writing to be enforceable",
        rationale: "Applies to real estate contracts, leases over one year, etc.",
        isCorrect: true
      },
      {
        text: "Notarized to be valid",
        rationale: "Notarization applies to deeds, not all contracts.",
        isCorrect: false
      },
      {
        text: "Recorded to be binding",
        rationale: "Recording isn't required for validity.",
        isCorrect: false
      },
      {
        text: "Approved by the DRE",
        rationale: "The DRE does not approve private contracts.",
        isCorrect: false
      }
    ],
    hint: "Real estate = must be in writing."
  },
  {
    questionNumber: 138,
    question: "An option contract gives the optionee the:",
    answerOptions: [
      {
        text: "Right to purchase property within a specified time at agreed terms",
        rationale: "The optionee holds a contractual right but not obligation to buy.",
        isCorrect: true
      },
      {
        text: "Obligation to buy the property",
        rationale: "It's a right, not a duty.",
        isCorrect: false
      },
      {
        text: "Right to occupy the property until purchase",
        rationale: "Occupancy is not included unless agreed separately.",
        isCorrect: false
      },
      {
        text: "Interest in title immediately",
        rationale: "No title until option exercised.",
        isCorrect: false
      }
    ],
    hint: "Option = right to buy, not requirement."
  },
  {
    questionNumber: 139,
    question: "In a lease, the lessor is the:",
    answerOptions: [
      {
        text: "Landlord who grants the lease",
        rationale: "Lessor = owner giving possession.",
        isCorrect: true
      },
      {
        text: "Tenant who receives the lease",
        rationale: "That's the lessee.",
        isCorrect: false
      },
      {
        text: "Subtenant in possession",
        rationale: "Subtenants lease from lessees, not lessors.",
        isCorrect: false
      },
      {
        text: "Property manager",
        rationale: "Manager acts as agent, not principal.",
        isCorrect: false
      }
    ],
    hint: "Lessor = landlord."
  },
  {
    questionNumber: 140,
    question: "A lease that automatically renews month to month after expiration is a:",
    answerOptions: [
      {
        text: "Periodic tenancy",
        rationale: "Renews for successive periods until notice given.",
        isCorrect: true
      },
      {
        text: "Tenancy at will",
        rationale: "Ends at any time without notice in some cases.",
        isCorrect: false
      },
      {
        text: "Estate for years",
        rationale: "Has fixed term with definite end.",
        isCorrect: false
      },
      {
        text: "Tenancy at sufferance",
        rationale: "Tenant remains unlawfully after lease expires.",
        isCorrect: false
      }
    ],
    hint: "Month-to-month = periodic."
  },
  {
    questionNumber: 141,
    question: "If a tenant remains in possession without landlord's consent after lease expiration, it creates a:",
    answerOptions: [
      {
        text: "Tenancy at sufferance",
        rationale: "Tenant is a holdover occupant without right.",
        isCorrect: true
      },
      {
        text: "Tenancy at will",
        rationale: "At will requires consent.",
        isCorrect: false
      },
      {
        text: "Periodic tenancy",
        rationale: "Renewal implies landlord consent.",
        isCorrect: false
      },
      {
        text: "Estate for years",
        rationale: "Ended already.",
        isCorrect: false
      }
    ],
    hint: "Sufferance = holdover."
  },
  {
    questionNumber: 142,
    question: "An assignment of a lease differs from a sublease because:",
    answerOptions: [
      {
        text: "Assignment transfers the tenant's entire interest to another party",
        rationale: "Sublease keeps part of the interest with the original tenant.",
        isCorrect: true
      },
      {
        text: "Assignment always requires landlord consent",
        rationale: "Depends on lease terms.",
        isCorrect: false
      },
      {
        text: "Sublease gives possession to the landlord",
        rationale: "No, possession goes to subtenant.",
        isCorrect: false
      },
      {
        text: "Sublease transfers all rights permanently",
        rationale: "Sublease = partial interest only.",
        isCorrect: false
      }
    ],
    hint: "Assignment = full transfer."
  },
  {
    questionNumber: 143,
    question: "A lease must be in writing to be enforceable if it is for:",
    answerOptions: [
      {
        text: "More than one year",
        rationale: "Statute of Frauds requires written lease over one year.",
        isCorrect: true
      },
      {
        text: "Exactly one year",
        rationale: "One year or less may be oral and still valid.",
        isCorrect: false
      },
      {
        text: "A commercial property",
        rationale: "Length, not type, determines writing requirement.",
        isCorrect: false
      },
      {
        text: "Any rental property",
        rationale: "Only long-term leases require writing.",
        isCorrect: false
      }
    ],
    hint: "Over one year = must be written."
  },
  {
    questionNumber: 144,
    question: "An earnest money deposit in a real estate offer serves to:",
    answerOptions: [
      {
        text: "Show good faith and may become liquidated damages if buyer defaults",
        rationale: "Deposit demonstrates intent to perform and secures contract.",
        isCorrect: true
      },
      {
        text: "Pay broker's commission in advance",
        rationale: "Broker's pay comes after closing.",
        isCorrect: false
      },
      {
        text: "Cover title insurance costs",
        rationale: "Separate closing expense.",
        isCorrect: false
      },
      {
        text: "Act as loan down payment",
        rationale: "Applied later but not same purpose.",
        isCorrect: false
      }
    ],
    hint: "Good faith deposit, not prepayment."
  },
  {
    questionNumber: 145,
    question: "A counteroffer made by a seller:",
    answerOptions: [
      {
        text: "Terminates the original offer and becomes a new offer",
        rationale: "A counteroffer legally rejects the previous one.",
        isCorrect: true
      },
      {
        text: "Modifies the original offer while keeping it open",
        rationale: "Once countered, the original offer is dead.",
        isCorrect: false
      },
      {
        text: "Is binding immediately",
        rationale: "Not binding until accepted by the buyer.",
        isCorrect: false
      },
      {
        text: "May be withdrawn only by court order",
        rationale: "Can be withdrawn anytime before acceptance.",
        isCorrect: false
      }
    ],
    hint: "Counter = rejection + new offer."
  },
  {
    questionNumber: 146,
    question: "If a buyer defaults, the seller's typical legal remedy is:",
    answerOptions: [
      {
        text: "Retention of the earnest money deposit as liquidated damages",
        rationale: "Allowed if clause initialed by both parties.",
        isCorrect: true
      },
      {
        text: "Revocation of the buyer's license",
        rationale: "Buyers aren't licensed by DRE.",
        isCorrect: false
      },
      {
        text: "Automatic specific performance",
        rationale: "Must be pursued in court, not automatic.",
        isCorrect: false
      },
      {
        text: "Criminal penalties",
        rationale: "Default is civil, not criminal.",
        isCorrect: false
      }
    ],
    hint: "Liquidated damages = default safety net."
  },
  {
    questionNumber: 147,
    question: "In California, an option to purchase real estate must:",
    answerOptions: [
      {
        text: "Be in writing and signed by the optionor",
        rationale: "Required under the Statute of Frauds.",
        isCorrect: true
      },
      {
        text: "Be recorded to be enforceable",
        rationale: "Recording not necessary for validity.",
        isCorrect: false
      },
      {
        text: "Include escrow instructions",
        rationale: "Escrow opens only upon exercise.",
        isCorrect: false
      },
      {
        text: "Be witnessed by a notary",
        rationale: "Notarization not required.",
        isCorrect: false
      }
    ],
    hint: "Options = written + signed."
  },
  {
    questionNumber: 148,
    question: "An executed contract means:",
    answerOptions: [
      {
        text: "All parties have fully performed their obligations",
        rationale: "Executed = completed; executory = still pending.",
        isCorrect: true
      },
      {
        text: "Only the buyer has signed",
        rationale: "Signatures ≠ execution of performance.",
        isCorrect: false
      },
      {
        text: "The contract is awaiting closing",
        rationale: "That's executory stage.",
        isCorrect: false
      },
      {
        text: "The agreement is void",
        rationale: "Executed doesn't mean invalid.",
        isCorrect: false
      }
    ],
    hint: "Executed = done deal."
  },
  {
    questionNumber: 149,
    question: "A unilateral contract is one where:",
    answerOptions: [
      {
        text: "Only one party makes a promise in exchange for an act",
        rationale: "Example: open listing — broker earns commission only upon performance.",
        isCorrect: true
      },
      {
        text: "Both parties exchange mutual promises",
        rationale: "That's bilateral.",
        isCorrect: false
      },
      {
        text: "It's signed by one party only",
        rationale: "Signature count doesn't define unilateral.",
        isCorrect: false
      },
      {
        text: "The broker represents both sides",
        rationale: "That's dual agency.",
        isCorrect: false
      }
    ],
    hint: "Uni = one promise for one act."
  },
  {
    questionNumber: 150,
    question: "An oral real estate listing is:",
    answerOptions: [
      {
        text: "Unenforceable in court",
        rationale: "Listings must be in writing to be enforceable under the Statute of Frauds.",
        isCorrect: true
      },
      {
        text: "Valid and binding if both agree",
        rationale: "Needs writing to protect commission rights.",
        isCorrect: false
      },
      {
        text: "Acceptable for lease transactions only",
        rationale: "Still requires writing over one year.",
        isCorrect: false
      },
      {
        text: "Enforceable if witnessed by two people",
        rationale: "Witnesses don't override legal writing requirement.",
        isCorrect: false
      }
    ],
    hint: "Listings = written or you lose your commission."
  }
];